    // === Submission deadline ===========================================
    // After this exact moment the whole bracket locks: no picks can be made
    // or changed, and no entry can be submitted.
    //
    // This is only a FALLBACK default (South Africa time). The live value comes
    // from the Google Sheet's "Config" tab (cell B2) via the bracket feed, so
    // the owner can change the lock date there without touching code.
    let SUBMISSION_DEADLINE = new Date("2026-06-29T21:00:00+02:00");
    let deadlineTimer = null;

    // === Predictions OPEN date =========================================
    // Before this moment the bracket is view-only: no picks/entries yet, and a
    // "predictions open on ..." banner shows. Also a FALLBACK default (SAST);
    // the live value comes from the Config tab (cell B3). A past date = open.
    let SUBMISSION_OPEN = new Date("2026-06-01T00:00:00+02:00");
    let openTimer = null;
    let countdownTimer = null;
    let bracketDatesReady_ = false;
    let bracketForceClosed_ = false;
    const BRACKET_DATES_CACHE_KEY = "bbBracketDatesCache";
    const BRACKET_STATE_CACHE_KEY = "bbBracketStateCache";
    const BRACKET_BOOT_RETRIES = 4;
    const BRACKET_BOOT_MAX_MS = 20000;
    const LB_PUBLIC_CACHE_KEY = "bbPublicStandingsCache";
    const LB_PUBLIC_BOOT_RETRIES = 3;

    function cacheBracketDates_(opens, deadline) {
      const close = String(deadline || "").trim();
      if (!close) return;
      try {
        window.localStorage.setItem(
          BRACKET_DATES_CACHE_KEY,
          JSON.stringify({
            opens: String(opens || "").trim(),
            deadline: close,
            savedAt: Date.now()
          })
        );
      } catch {
        /* ignore */
      }
    }

    function applyCachedBracketDates_() {
      try {
        const raw = window.localStorage.getItem(BRACKET_DATES_CACHE_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || !data.deadline) return false;
        applyOpenDate(data.opens);
        applyDeadline(data.deadline);
        return true;
      } catch {
        return false;
      }
    }

    function cacheBracketState_(state) {
      if (!state || !state.matches) return;
      try {
        window.localStorage.setItem(
          BRACKET_STATE_CACHE_KEY,
          JSON.stringify({
            matches: state.matches,
            opens: state.opens || "",
            deadline: state.deadline || "",
            savedAt: Date.now()
          })
        );
      } catch {
        /* ignore */
      }
    }

    function applyCachedBracketState_() {
      if (!isSubmissionEndpointConfigured()) return false;
      try {
        const raw = window.localStorage.getItem(BRACKET_STATE_CACHE_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || !data.matches) return false;
        applyBracketState(
          { ok: true, matches: data.matches, opens: data.opens, deadline: data.deadline },
          true
        );
        return true;
      } catch {
        return false;
      }
    }

    function sleepMs_(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    function setBracketDatesReady_() {
      if (bracketDatesReady_) {
        applyLockUi();
        return;
      }
      bracketDatesReady_ = true;
      const page = document.querySelector(".bb-page");
      if (page) {
        page.classList.add("is-bracket-ready");
      }
      scheduleDeadlineLock();
      scheduleOpenUnlock();
      applyLockUi();
    }

    // Update the deadline from the backend (Config tab). Accepts an ISO string
    // like "2026-07-10T23:59:00+02:00"; ignores blank/invalid values.
    function applyDeadline(value) {
      const raw = String(value || "").trim();
      if (!raw) {
        return;
      }
      const d = new Date(raw);
      if (isNaN(d.getTime()) || d.getTime() === SUBMISSION_DEADLINE.getTime()) {
        return;
      }
      SUBMISSION_DEADLINE = d;
      scheduleDeadlineLock();
    }

    // Update the open date from the backend (Config tab cell B3).
    function applyOpenDate(value) {
      const raw = String(value || "").trim();
      if (!raw) {
        return;
      }
      const d = new Date(raw);
      if (isNaN(d.getTime()) || d.getTime() === SUBMISSION_OPEN.getTime()) {
        return;
      }
      SUBMISSION_OPEN = d;
      scheduleOpenUnlock();
    }

    function isBracketLocked() {
      if (bracketForceClosed_) return true;
      const deadline = SUBMISSION_DEADLINE.getTime();
      return Number.isFinite(deadline) && Date.now() >= deadline;
    }

    function isBracketOpen() {
      const open = SUBMISSION_OPEN.getTime();
      return !Number.isFinite(open) || Date.now() >= open;
    }

    // True whenever picks/entries are NOT allowed: either not open yet, or
    // already locked. Used to gate picking and submitting.
    function predictionsClosed() {
      return !isBracketOpen() || isBracketLocked();
    }

    function formatDeadline() {
      try {
        const parts = new Intl.DateTimeFormat("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "Africa/Johannesburg"
        }).formatToParts(SUBMISSION_DEADLINE);
        const part = (type) => parts.find((p) => p.type === type)?.value || "";
        return `${part("day")} ${part("month")} ${part("year")} at ${part("hour")}:${part("minute")}`;
      } catch {
        return formatDateLong_(SUBMISSION_DEADLINE);
      }
    }

    function formatOpen() {
      try {
        const parts = new Intl.DateTimeFormat("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "Africa/Johannesburg"
        }).formatToParts(SUBMISSION_OPEN);
        const part = (type) => parts.find((p) => p.type === type)?.value || "";
        return `${part("weekday")} ${part("day")} ${part("month")} ${part("year")} at ${part("hour")}:${part("minute")}`;
      } catch {
        return formatDateLong_(SUBMISSION_OPEN);
      }
    }

    function formatDateLong_(date) {
      try {
        return new Intl.DateTimeFormat(undefined, {
          dateStyle: "long",
          timeStyle: "short"
        }).format(date);
      } catch {
        return date.toString();
      }
    }

    // === Where entries are sent ========================================
    // Paste your deployed Google Apps Script web-app URL here (it ends in
    // "/exec"). Until you do, submitting falls back to downloading a JSON
    // backup so no entry is ever lost. See bracket-backend.gs for the
    // server-side script and setup steps.
    const SUBMISSION_ENDPOINT = "https://script.google.com/macros/s/AKfycbzUGBlIP4WzWAtUSYLvBpe15K_ojTaadiiaOp2ryY2TxEQR9-jE7_p0mmiWTBFh7BvU/exec";

    function isSubmissionEndpointConfigured() {
      return (
        typeof SUBMISSION_ENDPOINT === "string" &&
        /^https:\/\//.test(SUBMISSION_ENDPOINT) &&
        !SUBMISSION_ENDPOINT.includes("REPLACE_WITH")
      );
    }

    // === One entry per browser (soft lock) =============================
    // After a successful submission we remember it in this browser's
    // localStorage so the same person isn't nudged to enter twice. This is
    // a deterrent only: it is per-browser and can be bypassed by clearing
    // site data, using a different browser/device, or private browsing.
    const ENTRY_FLAG_KEY = "bbWorldCup2026Entry";

    // The "already submitted" lock is confirmed against the BACKEND (the
    // database is the source of truth), not just this browser's localStorage.
    // Default false so nobody is ever wrongly blocked; we only lock once the
    // backend confirms an entry exists for the stored email. A stale local
    // flag (e.g. from a test entry that was later removed) self-clears.
    let confirmedSubmitted = false;

    function getStoredEntry() {
      try {
        const raw = window.localStorage.getItem(ENTRY_FLAG_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    }

    function clearStoredEntry() {
      try {
        window.localStorage.removeItem(ENTRY_FLAG_KEY);
      } catch {
        // Storage unavailable: nothing to clear.
      }
    }

    function hasAlreadySubmitted() {
      return confirmedSubmitted;
    }

    function rememberSubmission(payload) {
      try {
        window.localStorage.setItem(ENTRY_FLAG_KEY, JSON.stringify({
          submitted_at: payload.submitted_at,
          email: payload.email,
          champion: payload.champion
        }));
      } catch {
        // Storage unavailable (private mode / blocked): the soft lock just
        // won't persist for this visit. Submission still succeeds.
      }
    }

    // Verify the local "already submitted" flag against the database. If the
    // stored email isn't actually in the backend, the flag is stale — clear it
    // so the visitor can enter. Fails open (never blocks) if it can't check.
    async function refreshSubmittedState() {
      const stored = getStoredEntry();
      if (!stored || !stored.email || !isSubmissionEndpointConfigured()) {
        confirmedSubmitted = false;
        updateEntryButtonsState();
        return;
      }
      try {
        const res = await lbJsonp({ action: "check_email", email: stored.email });
        if (res && res.ok) {
          confirmedSubmitted = Boolean(res.exists);
          if (!res.exists) {
            clearStoredEntry();
          }
        }
      } catch {
        confirmedSubmitted = false;
      }
      updateEntryButtonsState();
      updateChampionPanel_();
    }

    const ROUND_LABELS = {
      r32: "Round of 32",
      r16: "Round of 16",
      qf: "Quarter-finals",
      sf: "Semi-finals",
      final: "Final"
    };

    function roundLabelForSlot(slot) {
      return ROUND_LABELS[slot] || slot;
    }

    const worldCupGroups = [
      { group: "A", teams: ["Mexico", "Korea Republic", "South Africa", "Czechia"] },
      { group: "B", teams: ["Canada", "Switzerland", "Qatar", "Bosnia and Herzegovina"] },
      { group: "C", teams: ["Brazil", "Morocco", "Scotland", "Haiti"] },
      { group: "D", teams: ["United States", "Australia", "Paraguay", "Türkiye"] },
      { group: "E", teams: ["Germany", "Curaçao", "Côte d'Ivoire", "Ecuador"] },
      { group: "F", teams: ["Netherlands", "Japan", "Tunisia", "Sweden"] },
      { group: "G", teams: ["Belgium", "IR Iran", "Egypt", "New Zealand"] },
      { group: "H", teams: ["Spain", "Uruguay", "Saudi Arabia", "Cape Verde"] },
      { group: "I", teams: ["France", "Senegal", "Norway", "Iraq"] },
      { group: "J", teams: ["Argentina", "Austria", "Algeria", "Jordan"] },
      { group: "K", teams: ["Portugal", "Colombia", "Uzbekistan", "DR Congo"] },
      { group: "L", teams: ["England", "Croatia", "Panama", "Ghana"] }
    ];

    function displayNameForGroupCard(teamName) {
      switch (teamName) {
        case "Korea Republic":
          return "South Korea";
        case "Czechia":
          return "Czech Republic";
        case "IR Iran":
          return "Iran";
        default:
          return teamName;
      }
    }


    const teamAbbreviations = {
      "Mexico": "MEX",
      "South Africa": "RSA",
      "Canada": "CAN",
      "Switzerland": "SUI",
      "Brazil": "BRA",
      "Scotland": "SCO",
      "United States": "USA",
      "Australia": "AUS",
      "Germany": "GER",
      "Côte d'Ivoire": "CIV",
      "Netherlands": "NED",
      "Japan": "JPN",
      "Belgium": "BEL",
      "Egypt": "EGY",
      "Spain": "ESP",
      "Uruguay": "URU",
      "France": "FRA",
      "Senegal": "SEN",
      "Argentina": "ARG",
      "Austria": "AUT",
      "Portugal": "POR",
      "Colombia": "COL",
      "England": "ENG",
      "Croatia": "CRO",
      "Czechia": "CZE",
      "Korea Republic": "KOR",
      "Qatar": "QAT",
      "Bosnia and Herzegovina": "BIH",
      "Morocco": "MAR",
      "Haiti": "HAI",
      "Paraguay": "PAR",
      "Türkiye": "TUR",
      "Curaçao": "CUW",
      "Ecuador": "ECU",
      "Sweden": "SWE",
      "Tunisia": "TUN",
      "IR Iran": "IRN",
      "New Zealand": "NZL",
      "Cape Verde": "CPV",
      "Saudi Arabia": "KSA",
      "Norway": "NOR",
      "Iraq": "IRQ",
      "Algeria": "ALG",
      "Jordan": "JOR",
      "DR Congo": "COD",
      "Uzbekistan": "UZB",
      "Ghana": "GHA",
      "Panama": "PAN"
    };


    const teamFlagCodes = {
      "Mexico": "mx",
      "South Africa": "za",
      "Canada": "ca",
      "Switzerland": "ch",
      "Brazil": "br",
      "Scotland": "gb-sct",
      "United States": "us",
      "Australia": "au",
      "Germany": "de",
      "Côte d'Ivoire": "ci",
      "Netherlands": "nl",
      "Japan": "jp",
      "Belgium": "be",
      "Egypt": "eg",
      "Spain": "es",
      "Uruguay": "uy",
      "France": "fr",
      "Senegal": "sn",
      "Argentina": "ar",
      "Austria": "at",
      "Portugal": "pt",
      "Colombia": "co",
      "England": "gb-eng",
      "Croatia": "hr",
      "Czechia": "cz",
      "Korea Republic": "kr",
      "Qatar": "qa",
      "Bosnia and Herzegovina": "ba",
      "Morocco": "ma",
      "Haiti": "ht",
      "Paraguay": "py",
      "Türkiye": "tr",
      "Curaçao": "cw",
      "Ecuador": "ec",
      "Sweden": "se",
      "Tunisia": "tn",
      "IR Iran": "ir",
      "New Zealand": "nz",
      "Cape Verde": "cv",
      "Saudi Arabia": "sa",
      "Norway": "no",
      "Iraq": "iq",
      "Algeria": "dz",
      "Jordan": "jo",
      "DR Congo": "cd",
      "Uzbekistan": "uz",
      "Ghana": "gh",
      "Panama": "pa"
    };

    function getTeamFlagCode(teamName) {
      return teamFlagCodes[teamName] || "";
    }

    function setHeroChampionDisplay(el, teamName) {
      if (!el) {
        return;
      }

      const name = getFullDisplayTeamName(teamName);
      const flagCode = getTeamFlagCode(teamName);
      el.replaceChildren();

      if (flagCode) {
        const flag = document.createElement("span");
        flag.className = "bb-champion__flag-hero fi fi-" + flagCode;
        flag.setAttribute("aria-hidden", "true");
        el.append(flag);
      }

      const nameEl = document.createElement("span");
      nameEl.className = "bb-champion__name";
      nameEl.textContent = name;
      el.append(nameEl);
    }

    function clearHeroChampionDisplay(el, text) {
      if (!el) {
        return;
      }
      el.replaceChildren();
      const nameEl = document.createElement("span");
      nameEl.className = "bb-champion__name";
      nameEl.textContent = text;
      el.append(nameEl);
    }


    function getDisplayTeamName(teamName) {
      if (isPlaceholder(teamName)) {
        return getDisplayPlaceholderName(teamName);
      }

      return teamAbbreviations[teamName] || teamName;
    }

    function getFullDisplayTeamName(teamName) {
      if (isPlaceholder(teamName)) {
        return getDisplayPlaceholderName(teamName);
      }

      return teamName;
    }

    function setChampionDisplayWithFlag(el, teamName) {
      if (!el) return;

      if (el.id === "bb-champion-display") {
        setHeroChampionDisplay(el, teamName);
        return;
      }

      const name = getFullDisplayTeamName(teamName);
      el.replaceChildren();

      const flagCode = getTeamFlagCode(teamName);
      if (flagCode) {
        const flag = document.createElement("span");
        flag.className = `bb-champion__flag fi fi-${flagCode}`;
        flag.setAttribute("aria-hidden", "true");
        el.append(flag);
      }

      el.append(document.createTextNode(name));
    }


    function getDisplayPlaceholderName(teamName) {
      const longMatch = /^Winner M(\d+)$/.exec(teamName || "");

      if (longMatch) {
        return `W${longMatch[1]}`;
      }

      return teamName;
    }

    // The third-place card shows the two semi-final losers. Derive them from the
    // current state of the semi-finals (the team in each SF that wasn't picked /
    // didn't win). Returns a placeholder like "L101" until a loser is known.
    function loserOfMatch(matchId) {
      const m = getMatch(matchId);
      if (!m) return "";
      const [a, b] = m.teams;
      const winner = picks[matchId];
      if (!winner || isPlaceholder(a) || isPlaceholder(b)) return "";
      return winner === a ? b : a;
    }

    function updateThirdPlaceTeams() {
      const third = getMatch("m103");
      if (!third) return;
      const initial = getInitialMatch("m103");
      third.teams[0] = loserOfMatch("m101") || initial.teams[0];
      third.teams[1] = loserOfMatch("m102") || initial.teams[1];
    }

    // === Live bracket state (from the Google Sheet "Bracket" tab) ========
    // The backend serves the real Round-of-32 teams + kickoff times (which the
    // owner updates as the group stage finishes) and the actual winners. Before
    // the deadline the board stays a prediction canvas (real R32 teams, user
    // picks advance). After the deadline it shows the actual tournament results.
    let bracketState = { matches: {} };
    const ACTUAL_WINNERS = {};

    // Kickoffs may arrive as plain text ("Fri, 3 Jul, 19:00") or — if the sheet
    // cell was typed as a real date — as a serialized date string containing
    // "GMT". Normalise the latter to a clean South-Africa-time label; pass
    // plain text through unchanged.
    function normalizeKickoff(value) {
      const raw = String(value || "").trim();
      if (!raw || !/GMT/i.test(raw)) {
        return raw;
      }
      const d = new Date(raw);
      if (isNaN(d.getTime())) {
        return raw;
      }
      try {
        return new Intl.DateTimeFormat("en-GB", {
          weekday: "short", day: "numeric", month: "short",
          hour: "2-digit", minute: "2-digit", hour12: false,
          timeZone: "Africa/Johannesburg"
        }).format(d);
      } catch {
        return raw;
      }
    }

    let bracketInitialLoadPending = true;
    let bracketStateReady_ = false;
    let skeletonPulseTimelines = [];
    let pageWireframeShownAt = 0;
    let heroIntroPlayed_ = false;
    let statsIntroPlayed_ = false;
    const PAGE_WIREFRAME_MIN_MS = 1400;
    const PAGE_WIREFRAME_MIN_CACHED_MS = 450;

    function hasCachedBracketBoot_() {
      try {
        if (window.localStorage.getItem(BRACKET_STATE_CACHE_KEY)) return true;
        if (window.localStorage.getItem(BRACKET_DATES_CACHE_KEY)) return true;
      } catch {
        /* ignore */
      }
      return false;
    }

    function wireframeMinMs_() {
      return hasCachedBracketBoot_() ? PAGE_WIREFRAME_MIN_CACHED_MS : PAGE_WIREFRAME_MIN_MS;
    }

    function themeColor(varName) {
      return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    }

    function markBracketStateReady_() {
      bracketStateReady_ = true;
    }

    async function waitForBracketStateReady_() {
      if (bracketStateReady_ || !isSubmissionEndpointConfigured()) {
        return;
      }
      const start = Date.now();
      while (!bracketStateReady_ && Date.now() - start < BRACKET_BOOT_MAX_MS) {
        await sleepMs_(50);
      }
      if (!bracketStateReady_) {
        markBracketStateReady_();
      }
    }

    function applyBracketState(state, skipRender) {
      if (!state) {
        return;
      }

      bracketForceClosed_ = false;
      applyOpenDate(state.opens);
      applyDeadline(state.deadline);
      cacheBracketDates_(state.opens, state.deadline);
      setBracketDatesReady_();

      if (!state.matches) {
        return;
      }
      bracketState = state;

      // 1) Sheet data per match: R32 teams (live) + kickoffs/status for all rounds.
      initialMatches.forEach((seed) => {
        const s = state.matches[seed.id];
        if (!s) {
          return;
        }
        const live = getMatch(seed.id);

        if (seed.slot === "r32") {
          const home = String(s.home || "").trim();
          const away = String(s.away || "").trim();
          if (home && away) {
            const changed = seed.teams[0] !== home || seed.teams[1] !== away;
            seed.teams = [home, away];
            if (live) {
              live.teams = [home, away];
              if (changed && picks[seed.id]) {
                delete picks[seed.id];
                resetDownstream(seed.id);
              }
            }
          }
        }

        const kick = normalizeKickoff(s.date);
        if (kick) {
          seed.kickoff = kick;
          if (live) {
            live.kickoff = kick;
          }
        }

        const sheetStatus = String(s.status || "").trim();
        if (sheetStatus) {
          seed.sheetStatus = sheetStatus;
          if (live) {
            live.sheetStatus = sheetStatus;
          }
        } else {
          delete seed.sheetStatus;
          if (live) {
            delete live.sheetStatus;
          }
        }
      });

      // 2) Actual winners (applied to the board only after the deadline).
      Object.keys(state.matches).forEach((id) => {
        const w = String(state.matches[id].winner || "").trim();
        if (w) {
          ACTUAL_WINNERS[id] = w;
        } else {
          delete ACTUAL_WINNERS[id];
        }
      });

      if (isBracketLocked()) {
        applyActualResults();
      }
      cacheBracketState_(state);
      if (!skipRender) {
        renderBracket();
      }
    }

    // Rebuild the board from real results: advance the actual winner of each
    // match through the tree. Used once predictions are locked.
    function applyActualResults() {
      matches = structuredClone(initialMatches);
      picks = {};
      initialMatches
        .filter((m) => !m.display)
        .forEach((seed) => {
          const winner = ACTUAL_WINNERS[seed.id];
          const live = getMatch(seed.id);
          if (!live || !winner || !live.teams.includes(winner)) {
            return;
          }
          picks[seed.id] = winner;
          if (live.nextMatch) {
            const nm = getMatch(live.nextMatch);
            if (nm) {
              nm.teams[live.nextSlot] = winner;
            }
          }
        });
    }

    async function syncClosedStateFromServer_() {
      if (isBracketLocked() || !isSubmissionEndpointConfigured()) {
        return isBracketLocked();
      }
      try {
        const res = await lbJsonp({ action: "public_standings" });
        if (res && res.ok) {
          bracketForceClosed_ = true;
          applyLockUi();
          return true;
        }
      } catch {
        /* bracket feed may be down while standings endpoint is up */
      }
      return isBracketLocked();
    }

    async function loadBracketState(options = {}) {
      const forBoot = options.forBoot === true;
      const maxAttempts = forBoot ? BRACKET_BOOT_RETRIES : 1;

      if (!isSubmissionEndpointConfigured()) {
        setBracketDatesReady_();
        markBracketStateReady_();
        return;
      }

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const res = await lbJsonp({ action: "bracket" });
          if (res && res.ok) {
            applyBracketState(res, forBoot);
            markBracketStateReady_();
            return;
          }
        } catch (err) {
          /* retry on boot when Apps Script is cold */
        }
        if (attempt < maxAttempts) {
          await sleepMs_(1200 * attempt);
        }
      }

      if (!bracketDatesReady_) {
        applyCachedBracketDates_();
        setBracketDatesReady_();
      }
      if (!isBracketLocked()) {
        await syncClosedStateFromServer_();
      } else {
        applyLockUi();
      }
      markBracketStateReady_();
    }

    function stopPageWireframePulse() {
      skeletonPulseTimelines.forEach((tl) => {
        if (tl) tl.kill();
      });
      skeletonPulseTimelines = [];
      const blocks = document.querySelectorAll(".bb-wf__block");
      if (!blocks.length || typeof gsap === "undefined") {
        return;
      }
      gsap.killTweensOf(blocks);
      gsap.set(blocks, { clearProps: "opacity,borderColor,boxShadow" });
    }

    function startPageWireframePulse() {
      stopPageWireframePulse();
      const blocks = document.querySelectorAll(".bb-wf__block");
      if (!blocks.length) {
        return;
      }

      if (prefersReducedMotion_() || typeof gsap === "undefined") {
        blocks.forEach((block) => {
          block.style.opacity = "0.55";
          block.style.borderColor = "rgba(22, 237, 246, 0.35)";
        });
        return;
      }

      const skelLow = "rgba(22, 237, 246, 0.22)";
      const skelMid = "rgba(22, 237, 246, 0.42)";
      const skelHigh = "rgba(22, 237, 246, 0.72)";

      gsap.set(blocks, {
        opacity: 0,
        borderColor: skelLow,
        boxShadow: "0 0 18px rgba(22, 237, 246, 0.06)"
      });

      gsap.to(blocks, {
        opacity: 0.42,
        duration: 0.35,
        stagger: 0.07,
        ease: "power2.out",
        onComplete: () => {
          blocks.forEach((block, i) => {
            const tl = gsap.timeline({ repeat: -1, delay: i * 0.1 });
            tl.to(block, {
              opacity: 0.78,
              borderColor: skelHigh,
              boxShadow: "0 0 22px rgba(22, 237, 246, 0.18)",
              duration: 1.1,
              ease: "sine.inOut"
            }).to(block, {
              opacity: 0.42,
              borderColor: skelMid,
              boxShadow: "0 0 12px rgba(22, 237, 246, 0.08)",
              duration: 1.1,
              ease: "sine.inOut"
            });
            skeletonPulseTimelines.push(tl);
          });
        }
      });
    }

    function showPageWireframe() {
      pageWireframeShownAt = Date.now();
      const page = document.querySelector(".bb-page");
      if (page) {
        page.classList.add("is-booting");
      }
      const wf = document.getElementById("bb-page-wf");
      if (wf) {
        wf.hidden = false;
        wf.setAttribute("aria-hidden", "true");
        wf.setAttribute("aria-busy", "true");
      }
      window.requestAnimationFrame(() => {
        if (typeof gsap === "undefined") {
          window.setTimeout(startPageWireframePulse, 40);
        } else {
          startPageWireframePulse();
        }
      });
      window.clearTimeout(showPageWireframe.safetyTimer_);
      showPageWireframe.safetyTimer_ = window.setTimeout(() => {
        markBracketStateReady_();
        finishPageWireframe();
      }, BRACKET_BOOT_MAX_MS);
    }

    function playHeroIntro_() {
      if (heroIntroPlayed_) {
        return;
      }

      const inner = document.querySelector(".bb-hero__slide-inner");
      if (!inner) {
        return;
      }

      heroIntroPlayed_ = true;

      const parts = [...inner.querySelectorAll("[data-hero-part]")];
      if (!parts.length) {
        inner.classList.remove("is-hero-waiting");
        return;
      }

      const title = inner.querySelector('[data-hero-part="title"]');
      const rest = parts.filter((el) => el !== title);

      if (prefersReducedMotion_() || typeof gsap === "undefined" || !title) {
        inner.classList.remove("is-hero-waiting");
        return;
      }

      gsap.set(parts, { opacity: 0 });

      gsap
        .timeline({
          onComplete: () => {
            inner.classList.remove("is-hero-waiting");
            gsap.set(parts, { clearProps: "opacity,transform" });
          }
        })
        .fromTo(title, { opacity: 0, x: -18 }, { opacity: 1, x: 0, duration: 0.7, ease: "power2.out" })
        .fromTo(
          rest,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.55, stagger: 0.09, ease: "power2.out" },
          "-=0.35"
        );
    }

    function setStatsFinalValues_() {
      const root = document.getElementById("bb-stats");
      if (!root) {
        return;
      }
      root.classList.remove("is-stats-waiting");
      root.querySelectorAll("[data-stat-value]").forEach((num) => {
        num.textContent = num.getAttribute("data-stat-value");
      });
      if (typeof gsap !== "undefined") {
        gsap.set(root.querySelectorAll(".bb-stat, [data-stat-value], .bb-stat span"), {
          clearProps: "opacity,transform,scale"
        });
      }
    }

    function playStatsIntro_() {
      if (statsIntroPlayed_) {
        return;
      }

      const root = document.getElementById("bb-stats");
      if (!root) {
        return;
      }

      statsIntroPlayed_ = true;

      const tiles = [...root.querySelectorAll(".bb-stat")];
      if (!tiles.length) {
        setStatsFinalValues_();
        return;
      }

      if (prefersReducedMotion_() || typeof gsap === "undefined") {
        setStatsFinalValues_();
        return;
      }

      root.classList.remove("is-stats-waiting");

      const tl = gsap.timeline({
        onComplete: () => {
          setStatsFinalValues_();
        }
      });

      tl.fromTo(tiles, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.55, stagger: 0.12, ease: "power2.out" }).fromTo(
        root.querySelectorAll(".bb-stat span"),
        { opacity: 0 },
        { opacity: 1, duration: 0.4, stagger: 0.12, ease: "power2.out" },
        "-=0.35"
      );

      tiles.forEach((tile, index) => {
        const num = tile.querySelector("[data-stat-value]");
        if (!num) {
          return;
        }

        const target = Number(num.getAttribute("data-stat-value"));
        const state = { val: 0 };

        tl.to(
          state,
          {
            val: target,
            duration: 0.85,
            ease: "power2.out",
            snap: { val: 1 },
            onUpdate: () => {
              num.textContent = String(Math.round(state.val));
            },
            onComplete: () => {
              num.textContent = String(target);
            }
          },
          0.1 + index * 0.12
        );
      });
    }

    async function finishPageWireframe() {
      if (!bracketInitialLoadPending) {
        return;
      }

      await waitForBracketStateReady_();

      const elapsed = Date.now() - pageWireframeShownAt;
      const wireframeMin = wireframeMinMs_();
      if (pageWireframeShownAt && elapsed < wireframeMin) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, wireframeMin - elapsed);
        });
      }

      bracketInitialLoadPending = false;
      stopPageWireframePulse();
      window.clearTimeout(showPageWireframe.safetyTimer_);
      const page = document.querySelector(".bb-page");
      if (page) {
        page.classList.remove("is-booting");
      }
      const wf = document.getElementById("bb-page-wf");
      if (wf) {
        wf.hidden = true;
        wf.removeAttribute("aria-busy");
      }
      renderBracket();
      window.requestAnimationFrame(() => {
        playHeroIntro_();
        playStatsIntro_();
      });
      flushResultsBoardAutoOpen_();
      flushLeaderboardAutoOpen_();
    }

    async function initBracketFromServer() {
      showPageWireframe();
      runAfterFirstPaint_(async () => {
        try {
          await Promise.all([
            loadBracketState({ forBoot: true }),
            restoreLbSession_()
          ]);
          if (!isBracketLocked()) {
            await syncClosedStateFromServer_();
          }
          await finishPageWireframe();
          if (isLbPublicClosedMode_()) {
            applyCachedPublicStandings_();
            ensurePublicStandingsReady_(false);
          }
        } catch (err) {
          console.error(err);
          await finishPageWireframe();
        }
      });
    }

    function startBracketAutoRefresh() {
      window.setInterval(loadBracketState, 5 * 60 * 1000);
    }

    const SUMMARY_BG_URL = "https://cdn.shopify.com/s/files/1/0457/5893/9300/files/summary-background-image-new.jpg?v=1782479543";

    function runAfterFirstPaint_(fn) {
      if (typeof fn !== "function") return;
      const run = () => {
        try {
          fn();
        } catch (err) {
          console.error(err);
        }
      };
      if ("requestIdleCallback" in window) {
        requestIdleCallback(run, { timeout: 2000 });
      } else {
        requestAnimationFrame(() => requestAnimationFrame(run));
      }
    }

    function lazyLoadSummaryBackground_() {
      const el = document.querySelector(".bb-champion");
      if (!el || el.classList.contains("is-bg-loaded")) return;
      const img = new Image();
      img.onload = () => el.classList.add("is-bg-loaded");
      img.src = SUMMARY_BG_URL;
    }

    function maybeRenderGroups_() {
      const section = document.getElementById("bb-groups-section");
      if (section && !section.classList.contains("d-none")) {
        renderGroups();
      }
    }


    // Bracket structure mirrors the workbook's "Bracket" tab (matches M73–M104).
    // R32 teams/kickoffs are the live defaults; the Google Sheet overrides them
    // (and fills actual winners) once results are entered. The list is ordered
    // top-to-bottom in bracket (leaf) order so the connector lines never cross.
    const initialMatches = [
      // Round of 32 (M73–M88) — in bracket order
      { id: "m74", slot: "r32", label: "M74", teams: ["Germany", "Paraguay"], kickoff: "Mon, 29 Jun, 22:30", nextMatch: "m89", nextSlot: 0 },
      { id: "m77", slot: "r32", label: "M77", teams: ["France", "Sweden"], kickoff: "Tue, 30 Jun, 23:00", nextMatch: "m89", nextSlot: 1 },
      { id: "m73", slot: "r32", label: "M73", teams: ["South Africa", "Canada"], kickoff: "Sun, 28 Jun, 21:00", nextMatch: "m90", nextSlot: 0 },
      { id: "m75", slot: "r32", label: "M75", teams: ["Netherlands", "Morocco"], kickoff: "Tue, 30 Jun, 03:00", nextMatch: "m90", nextSlot: 1 },
      { id: "m83", slot: "r32", label: "M83", teams: ["Portugal", "Ghana"], kickoff: "Fri, 3 Jul, 01:00", nextMatch: "m93", nextSlot: 0 },
      { id: "m84", slot: "r32", label: "M84", teams: ["Spain", "Austria"], kickoff: "Thu, 2 Jul, 21:00", nextMatch: "m93", nextSlot: 1 },
      { id: "m81", slot: "r32", label: "M81", teams: ["United States", "Bosnia and Herzegovina"], kickoff: "Thu, 2 Jul, 02:00", nextMatch: "m94", nextSlot: 0 },
      { id: "m82", slot: "r32", label: "M82", teams: ["Egypt", "Korea Republic"], kickoff: "Wed, 1 Jul, 22:00", nextMatch: "m94", nextSlot: 1 },
      { id: "m76", slot: "r32", label: "M76", teams: ["Brazil", "Japan"], kickoff: "Mon, 29 Jun, 19:00", nextMatch: "m91", nextSlot: 0 },
      { id: "m78", slot: "r32", label: "M78", teams: ["Côte d'Ivoire", "Norway"], kickoff: "Tue, 30 Jun, 19:00", nextMatch: "m91", nextSlot: 1 },
      { id: "m79", slot: "r32", label: "M79", teams: ["Mexico", "Scotland"], kickoff: "Wed, 1 Jul, 03:00", nextMatch: "m92", nextSlot: 0 },
      { id: "m80", slot: "r32", label: "M80", teams: ["England", "Cape Verde"], kickoff: "Wed, 1 Jul, 18:00", nextMatch: "m92", nextSlot: 1 },
      { id: "m86", slot: "r32", label: "M86", teams: ["Argentina", "Uruguay"], kickoff: "Fri, 3 Jul, 00:00", nextMatch: "m95", nextSlot: 0 },
      { id: "m88", slot: "r32", label: "M88", teams: ["Australia", "IR Iran"], kickoff: "Fri, 3 Jul, 19:00", nextMatch: "m95", nextSlot: 1 },
      { id: "m85", slot: "r32", label: "M85", teams: ["Switzerland", "Algeria"], kickoff: "Fri, 3 Jul, 05:00", nextMatch: "m96", nextSlot: 0 },
      { id: "m87", slot: "r32", label: "M87", teams: ["Colombia", "Croatia"], kickoff: "Sat, 4 Jul, 03:30", nextMatch: "m96", nextSlot: 1 },

      // Round of 16 (M89–M96)
      { id: "m89", slot: "r16", label: "M89", teams: ["W74", "W77"], nextMatch: "m97", nextSlot: 0 },
      { id: "m90", slot: "r16", label: "M90", teams: ["W73", "W75"], nextMatch: "m97", nextSlot: 1 },
      { id: "m93", slot: "r16", label: "M93", teams: ["W83", "W84"], nextMatch: "m98", nextSlot: 0 },
      { id: "m94", slot: "r16", label: "M94", teams: ["W81", "W82"], nextMatch: "m98", nextSlot: 1 },
      { id: "m91", slot: "r16", label: "M91", teams: ["W76", "W78"], nextMatch: "m99", nextSlot: 0 },
      { id: "m92", slot: "r16", label: "M92", teams: ["W79", "W80"], nextMatch: "m99", nextSlot: 1 },
      { id: "m95", slot: "r16", label: "M95", teams: ["W86", "W88"], nextMatch: "m100", nextSlot: 0 },
      { id: "m96", slot: "r16", label: "M96", teams: ["W85", "W87"], nextMatch: "m100", nextSlot: 1 },

      // Quarter-finals (M97–M100)
      { id: "m97", slot: "qf", label: "M97", teams: ["W89", "W90"], nextMatch: "m101", nextSlot: 0 },
      { id: "m98", slot: "qf", label: "M98", teams: ["W93", "W94"], nextMatch: "m101", nextSlot: 1 },
      { id: "m99", slot: "qf", label: "M99", teams: ["W91", "W92"], nextMatch: "m102", nextSlot: 0 },
      { id: "m100", slot: "qf", label: "M100", teams: ["W95", "W96"], nextMatch: "m102", nextSlot: 1 },

      // Semi-finals (M101–M102)
      { id: "m101", slot: "sf", label: "M101", teams: ["W97", "W98"], nextMatch: "m104", nextSlot: 0 },
      { id: "m102", slot: "sf", label: "M102", teams: ["W99", "W100"], nextMatch: "m104", nextSlot: 1 },

      // Final (M104)
      { id: "m104", slot: "final", label: "Final", teams: ["W101", "W102"], nextMatch: null, nextSlot: null },

      // Third-place play-off (M103) — display only, fed by the two semi-final
      // losers; not a prediction pick and not scored.
      { id: "m103", slot: "third", label: "M103", teams: ["L101", "L102"], display: true, nextMatch: null, nextSlot: null }
    ];

    // Match ids that count as predictions (everything except the display-only
    // third-place play-off). Used for pick counts and the submission payload.
    const PICK_SLOT_IDS = initialMatches.filter((m) => !m.display).map((m) => m.id);
    const TOTAL_PICKS = PICK_SLOT_IDS.length; // 31
    const CHAMPION_ID = "m104";

    let matches = structuredClone(initialMatches);
    let picks = {};
    let submitButtonsReady_ = false;
    let submitWasEnabled_ = false;

    const centerWinner = document.getElementById("bb-center-winner");
    const progressDisplay = document.getElementById("bb-progress-display");
    const toast = document.getElementById("bb-toast");

    function getMatch(matchId) {
      return matches.find((match) => match.id === matchId);
    }

    function getInitialMatch(matchId) {
      return initialMatches.find((match) => match.id === matchId);
    }

    function isPlaceholder(value) {
      return !value || value.startsWith("Winner ") || /^W\d+$/.test(value) || /^L\d+$/.test(value);
    }

    function showToast(message) {
      toast.textContent = message;
      toast.classList.add("is-visible");
      window.clearTimeout(showToast.timeout);
      showToast.timeout = window.setTimeout(() => {
        toast.classList.remove("is-visible");
      }, 2200);
    }

    function resetDownstream(matchId) {
      const sourceMatch = getMatch(matchId);

      if (!sourceMatch || !sourceMatch.nextMatch) {
        return;
      }

      const nextMatch = getMatch(sourceMatch.nextMatch);
      const initialNextMatch = getInitialMatch(sourceMatch.nextMatch);

      if (!nextMatch || !initialNextMatch) {
        return;
      }

      nextMatch.teams[sourceMatch.nextSlot] = initialNextMatch.teams[sourceMatch.nextSlot];

      if (picks[nextMatch.id]) {
        delete picks[nextMatch.id];
      }

      resetDownstream(nextMatch.id);
    }

    function selectWinner(matchId, teamName) {
      const match = getMatch(matchId);

      if (!match || match.display || isPlaceholder(teamName) || predictionsClosed()) {
        return;
      }

      picks[matchId] = teamName;
      resetDownstream(matchId);

      if (match.nextMatch) {
        const nextMatch = getMatch(match.nextMatch);
        nextMatch.teams[match.nextSlot] = teamName;
      }

      renderBracket();
    }

    function getTeamInitials(teamName) {
      if (isPlaceholder(teamName)) {
        return "W";
      }

      return teamName
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    }

    function canMatchBePicked(match) {
      return match.teams.every((team) => !isPlaceholder(team));
    }

    let onboardMatchId_ = null;

    function shouldShowFirstPickOnboard_() {
      if (reviewMode || predictionsClosed()) {
        return false;
      }
      return Object.keys(picks).length === 0;
    }

    function getFirstPickableMatchId_() {
      for (const match of matches) {
        if (!match.display && canMatchBePicked(match)) {
          return match.id;
        }
      }
      return null;
    }

    function normalizeSheetStatus(raw) {
      const value = String(raw || "").trim();
      if (!value) {
        return null;
      }
      const key = value.toLowerCase().replace(/\s+/g, " ");
      if (key === "confirmed") {
        return { key: "confirmed", label: "Confirmed" };
      }
      if (key === "as it stands" || key === "as its stands" || key.includes("as it stand") || key.includes("as its stand")) {
        return { key: "provisional", label: "As it stands" };
      }
      return { key: "provisional", label: value };
    }

    function appendMatchStatusBadge_(parent, key, label) {
      const badge = document.createElement("span");
      badge.className = `bb-match__sheet-status bb-match__sheet-status--${key}`;
      badge.textContent = label;
      parent.append(badge);
    }

    function appendMatchMetaLeft(meta, match) {
      const metaLeft = document.createElement("span");
      metaLeft.className = "bb-match__meta-left";

      const label = document.createElement("span");
      label.textContent = match.display ? "3rd place" : match.label;
      metaLeft.append(label);

      if (isClosedResultsView_()) {
        const actualWinner = ACTUAL_WINNERS[match.id] || null;
        if (actualWinner) {
          appendMatchStatusBadge_(metaLeft, "result", "Result");
        } else if (canMatchBePicked(match)) {
          appendMatchStatusBadge_(metaLeft, "upcoming", "Upcoming");
        }
        meta.append(metaLeft);
        return;
      }

      if (match.slot === "r32" && match.sheetStatus) {
        const sheetStatus = normalizeSheetStatus(match.sheetStatus);
        if (sheetStatus) {
          appendMatchStatusBadge_(metaLeft, sheetStatus.key, sheetStatus.label);
        }
      }

      const status = document.createElement("span");
      status.textContent = match.display
        ? "Play-off"
        : picks[match.id] ? "Picked" : canMatchBePicked(match) ? "Ready" : "Locked";

      meta.append(metaLeft, status);
    }

    function getTeamLabelForCard_(teamName) {
      if (isPlaceholder(teamName)) {
        return getDisplayTeamName(teamName);
      }
      return getFullDisplayTeamName(teamName);
    }

    function isClosedResultsView_() {
      return isBracketLocked() && !reviewMode;
    }

    function createMatchCard(match, direction) {
      const matchCard = document.createElement("article");
      matchCard.className = `bb-match bb-match--${direction}`;
      matchCard.dataset.matchId = match.id;
      if (onboardMatchId_ === match.id) {
        matchCard.classList.add("bb-match--onboard-active");
      }

      const meta = document.createElement("div");
      meta.className = "bb-match__meta";
      appendMatchMetaLeft(meta, match);

      if (match.kickoff) {
        const kickoff = document.createElement("div");
        kickoff.className = "bb-match__kickoff";
        kickoff.textContent = match.kickoff;
        matchCard.append(meta, kickoff);
      } else {
        matchCard.append(meta);
      }

      const teams = document.createElement("div");
      teams.className = "bb-match__teams";

      match.teams.forEach((teamName) => {
        const teamButton = document.createElement("button");
        teamButton.className = "bb-team";
        teamButton.type = "button";

        const actualWinner = ACTUAL_WINNERS[match.id] || null;
        const closedResults = isClosedResultsView_();
        const isPick = picks[match.id] === teamName;

        if (closedResults) {
          if (actualWinner && actualWinner === teamName) {
            teamButton.classList.add("is-actual-winner");
          } else if (actualWinner) {
            teamButton.classList.add("is-result-loser");
          }
        } else {
          const displayWinner = match.display ? ACTUAL_WINNERS[match.id] : null;
          if (isPick || (displayWinner && displayWinner === teamName)) {
            teamButton.classList.add("is-selected");
          }

          // My Bracket review: recolour the user's own pick by correctness.
          if (reviewMode && isPick && !match.display) {
            if (actualWinner) {
              teamButton.classList.add(actualWinner === teamName ? "is-correct" : "is-wrong");
            }
          }
        }

        if (isPlaceholder(teamName)) {
          teamButton.classList.add("is-placeholder");
        }

        teamButton.disabled = reviewMode || match.display || predictionsClosed() || isPlaceholder(teamName) || !canMatchBePicked(match);
        if (!match.display && !reviewMode && !closedResults) {
          teamButton.addEventListener("click", () => selectWinner(match.id, teamName));
        }

        const main = document.createElement("span");
        main.className = "bb-team__main";

        const badge = document.createElement("span");
        const flagCode = getTeamFlagCode(teamName);

        if (flagCode) {
          badge.className = `bb-team__flag fi fi-${flagCode}`;
          badge.setAttribute("aria-label", `${teamName} flag`);
        } else {
          badge.className = "bb-team__badge";
          badge.textContent = getTeamInitials(teamName);
        }

        const name = document.createElement("span");
        name.className = "bb-team__name";
        name.textContent = getTeamLabelForCard_(teamName);
        name.title = getFullDisplayTeamName(teamName);
        teamButton.title = getFullDisplayTeamName(teamName);

        const state = document.createElement("span");
        state.className = "bb-team__state";
        if (closedResults && actualWinner === teamName) {
          state.textContent = "✓";
        } else if (!closedResults && isPick) {
          if (reviewMode && actualWinner && actualWinner !== teamName) {
            state.textContent = "×";
          } else {
            state.textContent = "✓";
          }
        } else if (!closedResults && !reviewMode) {
          state.textContent = "›";
        } else {
          state.textContent = "";
        }

        main.append(badge, name);
        teamButton.append(main, state);
        teams.append(teamButton);
      });

      matchCard.append(teams);
      return matchCard;
    }

    function clearBracketContainers() {
      [
        "bb-col-r32",
        "bb-col-r16",
        "bb-col-qf",
        "bb-col-sf",
        "bb-center-final",
        "bb-col-third"
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = "";
      });
    }

    function renderBracket() {
      clearBracketContainers();
      updateThirdPlaceTeams();
      onboardMatchId_ = shouldShowFirstPickOnboard_() ? getFirstPickableMatchId_() : null;

      // Single left-to-right layout: every match goes into the column for its
      // round (R32, R16, QF, SF, Final), stacked top-to-bottom in match order.
      // The third-place play-off sits beneath the Final card.
      const containerMap = {
        r32: "bb-col-r32",
        r16: "bb-col-r16",
        qf: "bb-col-qf",
        sf: "bb-col-sf",
        final: "bb-center-final",
        third: "bb-col-third"
      };

      matches.forEach((match) => {
        const containerId = containerMap[match.slot];
        const container = document.getElementById(containerId);

        if (!container) {
          return;
        }

        container.append(createMatchCard(match, "left"));
      });

      updateSummary();

      window.requestAnimationFrame(() => {
        syncBracketLayout();
        scheduleAccordionRefresh(boardAccordion);
      });
    }

    const SLOT_ALIGN_ORDER = { r16: 1, qf: 2, sf: 3, final: 4 };

    function getFeederMatchIds(nextMatchId) {
      return initialMatches
        .filter((m) => m.nextMatch === nextMatchId)
        .sort((a, b) => a.nextSlot - b.nextSlot)
        .map((m) => m.id);
    }

    function alignBracketVerticalLayout() {
      const mirror = document.getElementById("bb-mirror");
      if (!mirror) {
        return;
      }

      const absStacks = mirror.querySelectorAll(".bb-stack--abs");
      absStacks.forEach((stack) => {
        stack.querySelectorAll(":scope > .bb-match").forEach((node) => {
          node.style.top = "";
        });
        stack.style.minHeight = "";
      });

      const centerCardReset = mirror.querySelector(".bb-center__card");
      if (centerCardReset) {
        centerCardReset.style.marginTop = "";
      }

      const r32Col = document.getElementById("bb-col-r32");
      const r32H = r32Col ? r32Col.offsetHeight : 0;

      const matchesToAlign = initialMatches
        .filter((m) => SLOT_ALIGN_ORDER[m.slot])
        .sort((a, b) => {
          const orderDiff = SLOT_ALIGN_ORDER[a.slot] - SLOT_ALIGN_ORDER[b.slot];
          return orderDiff !== 0 ? orderDiff : a.id.localeCompare(b.id);
        });

      for (const match of matchesToAlign) {
        const el = mirror.querySelector(`[data-match-id="${match.id}"]`);
        if (!el) {
          continue;
        }

        const feeders = getFeederMatchIds(match.id);
        if (feeders.length !== 2) {
          continue;
        }

        const aEl = mirror.querySelector(`[data-match-id="${feeders[0]}"]`);
        const bEl = mirror.querySelector(`[data-match-id="${feeders[1]}"]`);
        if (!aEl || !bEl) {
          continue;
        }

        const aRect = aEl.getBoundingClientRect();
        const bRect = bEl.getBoundingClientRect();
        const midY = (aRect.top + aRect.height / 2 + bRect.top + bRect.height / 2) / 2;

        const stack = el.parentElement;
        if (!stack || !stack.classList.contains("bb-stack--abs")) {
          continue;
        }

        const stackRect = stack.getBoundingClientRect();
        const h = el.getBoundingClientRect().height;
        let topPx = midY - h / 2 - stackRect.top;

        topPx = Math.max(0, topPx);
        el.style.top = `${topPx}px`;
      }

      const setStackMinHeight = (stackId, baseMin) => {
        const stack = document.getElementById(stackId);
        if (!stack || !stack.classList.contains("bb-stack--abs")) {
          return;
        }

        let maxBottom = 0;
        stack.querySelectorAll(":scope > .bb-match").forEach((node) => {
          const raw = node.style.top;
          if (!raw) {
            return;
          }

          const t = Number.parseFloat(raw);
          if (!Number.isFinite(t)) {
            return;
          }

          maxBottom = Math.max(maxBottom, t + node.offsetHeight);
        });

        const need = Math.max(baseMin, maxBottom + 12);
        stack.style.minHeight = `${Math.ceil(need)}px`;
      };

      setStackMinHeight("bb-col-r16", r32H);
      setStackMinHeight("bb-col-qf", r32H);
      setStackMinHeight("bb-col-sf", r32H);

      // Final: the champion card flows compactly (trophy + match together) and
      // keeps its "Final" header at the top of the column. Offset just the card
      // so its match lines up between the two semi-final matches.
      const centerCard = mirror.querySelector(".bb-center__card");
      const finalEl = mirror.querySelector('[data-match-id="m104"]');
      if (centerCard && finalEl) {
        const sfFeeders = getFeederMatchIds("m104");
        const aEl = mirror.querySelector(`[data-match-id="${sfFeeders[0]}"]`);
        const bEl = mirror.querySelector(`[data-match-id="${sfFeeders[1]}"]`);
        if (aEl && bEl) {
          const aRect = aEl.getBoundingClientRect();
          const bRect = bEl.getBoundingClientRect();
          const midY = (aRect.top + aRect.height / 2 + bRect.top + bRect.height / 2) / 2;
          const finalRect = finalEl.getBoundingClientRect();
          const finalCenter = finalRect.top + finalRect.height / 2;
          const mt = Math.max(0, Math.round(midY - finalCenter));
          centerCard.style.marginTop = `${mt}px`;
        }
      }
    }


    function drawBracketLines() {
      const mirror = document.getElementById("bb-mirror");
      const svg = document.getElementById("bb-lines");

      if (!mirror || !svg) {
        return;
      }

      svg.innerHTML = "";

      const mirrorRect = mirror.getBoundingClientRect();
      const mirrorWidth = mirrorRect.width;
      const mirrorHeight = mirrorRect.height;

      svg.setAttribute("viewBox", `0 0 ${mirrorWidth} ${mirrorHeight}`);
      svg.setAttribute("width", mirrorWidth);
      svg.setAttribute("height", mirrorHeight);

      const toLocalRect = (element) => {
        const rect = element.getBoundingClientRect();
        return {
          left: rect.left - mirrorRect.left,
          right: rect.right - mirrorRect.left,
          top: rect.top - mirrorRect.top,
          bottom: rect.bottom - mirrorRect.top,
          width: rect.width,
          height: rect.height,
          centerY: rect.top - mirrorRect.top + rect.height / 2
        };
      };

      const createPath = (x1, y1, x2, y2) => {
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        const midX = x1 + (x2 - x1) * 0.52;
        path.setAttribute("d", `M ${x1.toFixed(2)} ${y1.toFixed(2)} H ${midX.toFixed(2)} V ${y2.toFixed(2)} H ${x2.toFixed(2)}`);
        svg.append(path);
      };

      matches.forEach((match) => {
        if (!match.nextMatch) {
          return;
        }

        const sourceEl = mirror.querySelector(`[data-match-id="${match.id}"]`);
        const targetEl = mirror.querySelector(`[data-match-id="${match.nextMatch}"]`);

        if (!sourceEl || !targetEl) {
          return;
        }

        const sourceRect = toLocalRect(sourceEl);
        const targetRect = toLocalRect(targetEl);

        // Single direction: every connector runs left (source right edge) to
        // right (target left edge).
        createPath(sourceRect.right, sourceRect.centerY, targetRect.left, targetRect.centerY);
      });
    }

    // === Round stepper ================================================
    // Instead of free horizontal scrolling, the bracket is revealed one round
    // at a time. The page measures how many round-columns fit; if all fit
    // (wide desktop) the buttons hide and nothing shifts. Otherwise the
    // .bb-mirror is shifted with translateX so each tap reveals the next round.
    const ROUND_LABEL_BY_KEY = {
      r32: "Round of 32",
      r16: "Round of 16",
      qf: "Quarter-finals",
      sf: "Semi-finals",
      final: "Final"
    };

    let roundStep = 0;
    let roundMaxStep = 0;
    let stickyRoundsNavUpdate = function () {};
    let stickyBoardTopUpdate = function () {};
    let smoothScrollTo_ = null;

    function measureStickyHeaderOffset_(el) {
      const height = el.offsetHeight || el.getBoundingClientRect().height;
      return height > 0 ? Math.ceil(height) : 0;
    }

    function getPageHeaderOffset_() {
      const htmlPad = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const bodyPad = parseFloat(getComputedStyle(document.body).scrollPaddingTop) || 0;
      const themePad = Math.max(htmlPad, bodyPad);
      if (themePad > 0) {
        document.documentElement.style.setProperty("--bb-page-header-offset", Math.ceil(themePad) + "px");
        return Math.ceil(themePad);
      }

      let offset = 0;
      const stickyHeaderSelectors = [
        "store-header.header[sticky]",
        "store-header.header",
        "store-header",
        ".shopify-section-header-sticky",
        ".header-wrapper",
        "sticky-header"
      ];

      for (const selector of stickyHeaderSelectors) {
        const el = document.querySelector(selector);
        if (!el || el.closest(".bb-page")) continue;
        const measured = measureStickyHeaderOffset_(el);
        if (measured > 0) {
          offset = measured;
          break;
        }
      }

      if (!offset) {
        const varHeight = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--header-height")
        );
        if (varHeight > 0) {
          offset = Math.ceil(varHeight);
        }
      }

      if (!offset) {
        const groupedHeader = document.querySelector(
          ".shopify-section-group-header-group store-header, .shopify-section-group-header-group .header-wrapper, .shopify-section-group-header-group sticky-header"
        );
        if (groupedHeader) {
          offset = measureStickyHeaderOffset_(groupedHeader);
        }
      }

      if (!offset) {
        let tallest = 0;
        document.querySelectorAll(".shopify-section-group-header-group").forEach((el) => {
          if (el.closest(".bb-page")) return;
          const measured = measureStickyHeaderOffset_(el);
          if (measured > tallest) {
            tallest = measured;
          }
        });
        if (tallest > 0) {
          offset = tallest;
        }
      }

      if (!offset) {
        const hasShopifyHeader = document.querySelector(".shopify-section-group-header-group, store-header");
        if (hasShopifyHeader) {
          offset = 77;
        }
      }

      document.documentElement.style.setProperty("--bb-page-header-offset", offset + "px");
      return offset;
    }

    function getElementDocumentTop_(el) {
      let top = 0;
      let node = el;
      while (node) {
        top += node.offsetTop;
        node = node.offsetParent;
      }
      return top;
    }

    function getScrollHeaderInset_() {
      const varHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--header-height")
      );
      if (varHeight > 0) {
        return Math.ceil(varHeight);
      }

      const stickyHeaderSelectors = [
        "store-header.header[sticky]",
        "store-header.header",
        "store-header",
        ".cc-header store-header",
        ".shopify-section-header-sticky .header-wrapper",
        ".header-wrapper",
        "sticky-header"
      ];

      for (const selector of stickyHeaderSelectors) {
        const el = document.querySelector(selector);
        if (!el || el.closest(".bb-page")) continue;
        const measured = measureStickyHeaderOffset_(el);
        if (measured > 0) {
          return measured;
        }
      }

      return 77;
    }

    function applyLeaderboardScrollMargin_(section) {
      const inset = getScrollHeaderInset_();
      document.documentElement.style.setProperty("--bb-page-header-offset", inset + "px");
      section.style.scrollMarginTop = inset + "px";
      return inset;
    }

    function getLeaderboardScrollTarget_(section) {
      const inset = applyLeaderboardScrollMargin_(section);
      return Math.max(0, getElementDocumentTop_(section) - inset);
    }

    function getRoundColumns() {
      const mirror = document.getElementById("bb-mirror");
      return mirror ? Array.from(mirror.querySelectorAll(":scope > .bb-col--round")) : [];
    }

    function updateRoundsNavPicksLabel_() {
      const label = document.getElementById("bb-rounds-label");
      if (!label) return;
      const n = Object.keys(picks).length;
      label.innerHTML = 'Picks <span class="bb-rounds-nav__label-em">' + n + "</span>/" + TOTAL_PICKS;
    }

    function updateRoundStepper() {
      const wrap = document.getElementById("bb-mirror-wrap");
      const mirror = document.getElementById("bb-mirror");
      const nav = document.getElementById("bb-rounds-nav");
      const cols = getRoundColumns();

      if (!wrap || !mirror || cols.length === 0) {
        return;
      }

      // offsetWidth / offsetLeft ignore any CSS transform, so they are safe to
      // measure even while a translateX is applied.
      const wrapW = wrap.clientWidth;
      const mirrorW = mirror.offsetWidth;
      const baseLeft = cols[0].offsetLeft;
      const offsets = cols.map((c) => c.offsetLeft - baseLeft);

      const fits = mirrorW <= wrapW + 1;

      if (fits) {
        roundStep = 0;
        roundMaxStep = 0;
        mirror.style.transform = "";
        wrap.classList.remove("is-stepping");
        if (nav) nav.hidden = true;
        return;
      }

      wrap.classList.add("is-stepping");
      if (nav) nav.hidden = false;

      const maxTranslate = Math.max(0, mirrorW - wrapW);

      // The last step scrolls fully to the end so the (wide) Final column is
      // revealed completely. roundMaxStep is the first column whose left edge
      // reaches the maximum scroll; stepping there clamps to maxTranslate.
      roundMaxStep = cols.length - 1;
      for (let i = 0; i < offsets.length; i++) {
        if (offsets[i] >= maxTranslate - 0.5) {
          roundMaxStep = i;
          break;
        }
      }

      roundStep = Math.min(Math.max(0, roundStep), roundMaxStep);
      const tx = Math.min(offsets[roundStep], maxTranslate);
      mirror.style.transform = `translateX(${(-tx).toFixed(1)}px)`;

      const prevBtn = document.getElementById("bb-rounds-prev");
      const nextBtn = document.getElementById("bb-rounds-next");
      const label = document.getElementById("bb-rounds-label");

      if (prevBtn) prevBtn.disabled = roundStep <= 0;
      if (nextBtn) nextBtn.disabled = roundStep >= roundMaxStep;
      if (label) {
        if (window.matchMedia("(max-width: 1023px)").matches) {
          updateRoundsNavPicksLabel_();
        } else {
          const key = cols[roundStep].dataset.round;
          label.textContent = ROUND_LABEL_BY_KEY[key] || "";
        }
      }
    }

    function stepRounds(direction) {
      roundStep = Math.min(Math.max(0, roundStep + direction), roundMaxStep);
      updateRoundStepper();
    }

    function syncBracketLayout() {
      alignBracketVerticalLayout();
      drawBracketLines();
      updateRoundStepper();
      stickyRoundsNavUpdate();
      stickyBoardTopUpdate();
    }


    function updateEntryButtonsState() {
      const topBtn = document.getElementById("bb-open-entry-button");
      const bottomBtn = document.getElementById("bb-open-entry-button-bottom");
      const alreadySubmitted = hasAlreadySubmitted();
      const enabled = Object.keys(picks).length === TOTAL_PICKS && !predictionsClosed() && !alreadySubmitted;
      const label = alreadySubmitted ? "Entry submitted" : "Submit entry";

      if (submitButtonsReady_ && enabled && !submitWasEnabled_ && !alreadySubmitted) {
        fireSubmitCelebration_();
      }

      if (topBtn) {
        topBtn.disabled = !enabled;
        topBtn.setAttribute("aria-disabled", String(!enabled));
        topBtn.textContent = label;
      }

      if (bottomBtn) {
        bottomBtn.disabled = !enabled;
        bottomBtn.setAttribute("aria-disabled", String(!enabled));
        bottomBtn.classList.toggle("bb-submit-panel__cta--disabled", !enabled);
        bottomBtn.textContent = label;
      }

      const note = document.getElementById("bb-submitted-note");
      if (note) {
        note.hidden = !alreadySubmitted;
      }

      submitWasEnabled_ = enabled;
      submitButtonsReady_ = true;
      updateSubmitDock_();
    }

    const SUBMIT_ALERT_BTN_IDS_ = ["bb-open-entry-button", "bb-open-entry-button-bottom", "bb-submit-dock-btn"];

    function fireSubmitBreathe_(btn) {
      if (!btn) return;
      btn.classList.remove("is-submit-breathe");
      void btn.offsetWidth;
      btn.classList.add("is-submit-breathe");
      window.setTimeout(() => btn.classList.remove("is-submit-breathe"), 1150);
    }

    function fireSubmitCelebration_() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }
      SUBMIT_ALERT_BTN_IDS_.forEach((id) => {
        fireSubmitBreathe_(document.getElementById(id));
      });
    }

    function updateSubmitDock_() {
      const dock = document.getElementById("bb-submit-dock");
      const dockBtn = document.getElementById("bb-submit-dock-btn");
      const page = document.querySelector(".bb-page");
      if (!dock) return;

      const mobile = window.matchMedia("(max-width: 1023px)").matches;
      const complete = Object.keys(picks).length === TOTAL_PICKS && Boolean(picks[CHAMPION_ID]);
      const alreadySubmitted = hasAlreadySubmitted();
      const entryModalEl = document.getElementById("bb-entry-modal");
      const modalOpen = entryModalEl && entryModalEl.classList.contains("is-open");
      const canShow = mobile && complete && !predictionsClosed() && !alreadySubmitted && !reviewMode && !modalOpen;

      dock.classList.toggle("is-visible", canShow);
      dock.setAttribute("aria-hidden", canShow ? "false" : "true");
      if (page) page.classList.toggle("is-submit-dock-visible", canShow);

      if (dockBtn) {
        dockBtn.disabled = !canShow;
        dockBtn.textContent = alreadySubmitted ? "Entry submitted" : "Submit entry";
      }
    }

    function updateBoardHeading_() {
      const triggerTitle = document.getElementById("bb-board-trigger-title");
      const triggerCopy = document.getElementById("bb-board-trigger-copy");
      const triggerBadge = document.getElementById("bb-board-trigger-badge");

      let title;
      let helpText;
      let badge;

      if (isBracketLocked()) {
        title = "The Results";
        helpText = "Road to the final.";
        badge = "Live results";
      } else {
        title = "Pick Your Winners";
        helpText = "Click on the winner of each match.";
        badge = "31 picks";
      }

      if (triggerTitle) triggerTitle.textContent = title;
      if (triggerCopy) triggerCopy.textContent = helpText;
      if (triggerBadge) triggerBadge.textContent = badge;
    }

    function applyLockUi() {
      const openBanner = document.getElementById("bb-open-banner");
      const openDateEl = document.getElementById("bb-open-date");
      if (openDateEl) {
        openDateEl.textContent = formatOpen();
      }
      if (openBanner) {
        openBanner.hidden = isBracketOpen() || isBracketLocked();
      }

      // Closed state: hide pick/submit chrome; keep results bracket.
      const board = document.getElementById("bb-board-section");
      if (board) {
        board.classList.toggle("bb-board--closed", isBracketLocked());
      }

      updateBoardHeading_();
      updateCountdown();
      stickyBoardTopUpdate();
      scheduleAccordionRefresh(boardAccordion);
      updateLbChrome_();
      updateLeaderboardTriggerCopy_();
      if (isLbPublicClosedMode_()) {
        ensurePublicStandingsReady_(false);
      }
    }

    function animateProgressFill(pct) {
      const fill = document.getElementById("bb-progress-fill");
      if (!fill) {
        return;
      }
      const clamped = Math.max(0, Math.min(1, pct));
      if (prefersReducedMotion_() || typeof gsap === "undefined") {
        fill.style.transform = "scaleX(" + clamped + ")";
        return;
      }
      gsap.to(fill, { scaleX: clamped, duration: 0.5, ease: "power2.out" });
    }

    function updateSummary() {
      const pickedCount = Object.keys(picks).length;

      if (progressDisplay) {
        progressDisplay.textContent = `${pickedCount} / ${TOTAL_PICKS}`;
      }
      if (window.matchMedia("(max-width: 1023px)").matches) {
        updateRoundsNavPicksLabel_();
      }
      animateProgressFill(pickedCount / TOTAL_PICKS);
      centerWinner.title = picks[CHAMPION_ID] || "";

      if (picks[CHAMPION_ID]) {
        setChampionDisplayWithFlag(centerWinner, picks[CHAMPION_ID]);
      } else {
        centerWinner.textContent = "Champion pending";
      }
      updateEntryButtonsState();
      applyLockUi();
      updateChampionPanel_();
    }

    function autoPickRandomBracket() {
      for (const id of initialMatches.map((match) => match.id)) {
        const match = getMatch(id);
        const pickableTeams = match.teams.filter((team) => !isPlaceholder(team));

        if (pickableTeams.length === 2) {
          const randomTeam = pickableTeams[Math.floor(Math.random() * pickableTeams.length)];
          selectWinner(match.id, randomTeam);
        }
      }

      showToast("Random bracket generated.");
    }

    function renderGroups() {
      const groupsGrid = document.getElementById("bb-groups-grid");
      groupsGrid.innerHTML = "";

      worldCupGroups.forEach((groupData) => {
        const card = document.createElement("article");
        card.className = "bb-group-card bb-accordion-animate-in";

        const header = document.createElement("div");
        header.className = "bb-group-card__header";

        const title = document.createElement("h3");
        title.className = "bb-group-card__title";
        title.textContent = `Group ${groupData.group}`;

        const hint = document.createElement("span");
        hint.className = "bb-group-card__hint";
        hint.textContent = "4 teams";

        header.append(title, hint);

        const list = document.createElement("ol");
        list.className = "bb-group-card__teams";

        groupData.teams.forEach((team, index) => {
          const item = document.createElement("li");
          item.className = "bb-group-card__team";

          const seed = document.createElement("span");
          seed.className = "bb-group-card__seed";
          seed.textContent = `${groupData.group}${index + 1}`;

          const name = document.createElement("span");
          name.className = "bb-group-card__name";
          name.textContent = displayNameForGroupCard(team);
          name.title = team;

          item.append(seed, name);
          list.append(item);
        });

        card.append(header, list);
        groupsGrid.append(card);
      });
    }

    let groupsAccordion = null;
    let leaderboardAccordion = null;
    let scoringAccordion = null;
    let boardAccordion = null;

    const LB_BG_OPACITY_OPEN = 1;
    const LB_BG_OPACITY_CLOSED = 0;
    const ACCORDION_OPEN_EASE = "back.out(1.35)";
    const ACCORDION_OPEN_DURATION_MIN = 0.7;
    const ACCORDION_OPEN_DURATION_MAX = 1.15;
    const ACCORDION_OPEN_HEIGHT_REF = 900;

    function accordionOpenDuration_(scrollHeight) {
      if (!scrollHeight || scrollHeight <= ACCORDION_OPEN_HEIGHT_REF) {
        return ACCORDION_OPEN_DURATION_MIN;
      }
      return Math.min(
        ACCORDION_OPEN_DURATION_MAX,
        ACCORDION_OPEN_DURATION_MIN * (scrollHeight / ACCORDION_OPEN_HEIGHT_REF)
      );
    }

    function prefersReducedMotion_() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function initBbAccordion(cfg) {
      const root = document.getElementById(cfg.rootId);
      const btn = document.getElementById(cfg.btnId);
      let panel = document.getElementById(cfg.panelId);
      if (!root || !btn || !panel) return null;

      let inner = panel.querySelector(cfg.innerSelector);
      let items = cfg.itemSelector ? panel.querySelectorAll(cfg.itemSelector) : [];
      const chevronBtn = cfg.chevronBtnId ? document.getElementById(cfg.chevronBtnId) : null;
      const iconBtn = chevronBtn
        ? chevronBtn.querySelector(cfg.iconSelector)
        : btn.querySelector(cfg.iconSelector);

      function getStaggerItems_() {
        if (items.length) return items;
        if (cfg.staggerInnerChildren && inner) {
          return inner.querySelectorAll(":scope > *");
        }
        return [];
      }

      function notifyOpenState_(open) {
        if (open) {
          if (typeof cfg.onAfterOpen === "function") cfg.onAfterOpen();
        } else if (typeof cfg.onAfterClose === "function") {
          cfg.onAfterClose();
        }
      }

      function notifyClosing_() {
        if (typeof cfg.onBeforeClose === "function") cfg.onBeforeClose();
      }

      function refreshPanelRefs_(nextPanel) {
        if (!nextPanel) return;
        if (typeof gsap !== "undefined") {
          gsap.killTweensOf([panel, ...items]);
        }
        panel = nextPanel;
        inner = panel.querySelector(cfg.innerSelector);
        items = cfg.itemSelector ? panel.querySelectorAll(cfg.itemSelector) : [];
      }

      function animateRootBg(open) {
        if (!cfg.rootBgFade) return;
        const target = open ? LB_BG_OPACITY_OPEN : LB_BG_OPACITY_CLOSED;
        if (prefersReducedMotion_() || typeof gsap === "undefined") {
          root.style.setProperty("--lb-bg-opacity", String(target));
          return;
        }
        gsap.killTweensOf(root);
        gsap.to(root, {
          "--lb-bg-opacity": target,
          duration: open ? 0.55 : 0.45,
          ease: "power2.inOut"
        });
      }

      if (cfg.rootBgFade) {
        root.style.setProperty("--lb-bg-opacity", String(LB_BG_OPACITY_CLOSED));
      }

      let heightTweening_ = false;
      let pendingHeightRefresh_ = false;

      function finishHeightOpen_() {
        heightTweening_ = false;
        if (typeof gsap !== "undefined") {
          gsap.set(panel, { height: "auto" });
        } else {
          panel.style.height = "auto";
        }
        if (pendingHeightRefresh_) {
          pendingHeightRefresh_ = false;
          refreshHeight({ animate: true });
        }
      }

      function setOpen(open) {
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        if (chevronBtn) chevronBtn.setAttribute("aria-expanded", open ? "true" : "false");
        root.classList.toggle("is-open", open);
      }

      function pulseIcon() {
        if (prefersReducedMotion_() || typeof gsap === "undefined" || !iconBtn) return;
        gsap.killTweensOf(iconBtn);
        gsap.fromTo(iconBtn, { scale: 1 }, { scale: 1.06, duration: 0.2, ease: "power2.out", yoyo: true, repeat: 1 });
      }

      function refreshHeight(options = {}) {
        if (!root.classList.contains("is-open") || !inner) return;
        const target = inner.scrollHeight;
        if (prefersReducedMotion_() || typeof gsap === "undefined") {
          panel.style.height = "auto";
          return;
        }
        if (heightTweening_ && !options.force) {
          pendingHeightRefresh_ = true;
          return;
        }
        const current = Number(gsap.getProperty(panel, "height")) || 0;
        if (!options.animate && Math.abs(current - target) < 3) {
          gsap.set(panel, { height: "auto" });
          return;
        }
        if (options.animate) {
          heightTweening_ = true;
          gsap.to(panel, {
            height: target,
            duration: 0.42,
            ease: "power2.out",
            onComplete: finishHeightOpen_
          });
          return;
        }
        gsap.set(panel, { height: target });
      }

      function openPanel() {
        if (root.classList.contains("is-open")) return;
        if (typeof cfg.onOpen === "function") cfg.onOpen();

        if (prefersReducedMotion_() || typeof gsap === "undefined") {
          panel.style.height = "auto";
          getStaggerItems_().forEach((el) => {
            el.style.opacity = "1";
            el.style.transform = "none";
          });
          setOpen(true);
          animateRootBg(true);
          notifyOpenState_(true);
          return;
        }
        gsap.killTweensOf([panel, iconBtn, ...items]);
        setOpen(true);
        animateRootBg(true);
        pulseIcon();
        heightTweening_ = true;
        pendingHeightRefresh_ = false;
        const targetH = inner.scrollHeight;
        const staggerItems = getStaggerItems_();
        gsap.to(panel, {
          height: targetH,
          duration: accordionOpenDuration_(targetH),
          ease: ACCORDION_OPEN_EASE,
          onComplete: finishHeightOpen_
        });
        notifyOpenState_(true);
        if (staggerItems.length) {
          gsap.fromTo(staggerItems, { opacity: 0, y: 12 }, {
            opacity: 1,
            y: 0,
            duration: 0.45,
            stagger: 0.06,
            ease: "back.out(1.4)",
            delay: 0.08
          });
        }
      }

      function closePanel() {
        if (prefersReducedMotion_() || typeof gsap === "undefined") {
          setOpen(false);
          notifyClosing_();
          panel.style.height = "0px";
          getStaggerItems_().forEach((el) => { el.style.opacity = "0"; });
          animateRootBg(false);
          if (typeof cfg.onClose === "function") cfg.onClose();
          notifyOpenState_(false);
          return;
        }
        gsap.killTweensOf([panel, iconBtn, ...items]);
        setOpen(false);
        notifyClosing_();
        animateRootBg(false);
        if (typeof cfg.onClose === "function") cfg.onClose();
        const staggerItems = getStaggerItems_();
        if (staggerItems.length) {
          gsap.to(staggerItems, { opacity: 0, y: 8, duration: 0.2, stagger: 0.03, ease: "power2.in" });
        }
        pulseIcon();
        heightTweening_ = true;
        pendingHeightRefresh_ = false;
        gsap.to(panel, {
          height: 0,
          duration: 0.45,
          ease: "power2.inOut",
          onComplete: () => {
            heightTweening_ = false;
            notifyOpenState_(false);
          }
        });
      }

      function togglePanel_() {
        if (root.classList.contains("is-open")) {
          closePanel();
          return;
        }
        if (typeof cfg.beforeOpen === "function" && cfg.beforeOpen() === false) return;
        openPanel();
      }

      btn.addEventListener("click", togglePanel_);
      if (chevronBtn) chevronBtn.addEventListener("click", togglePanel_);

      return { open: openPanel, close: closePanel, refreshHeight, setPanelNode: refreshPanelRefs_ };
    }

    function setupGroupsAccordion() {
      groupsAccordion = initBbAccordion({
        rootId: "bb-groups-section",
        btnId: "bb-groups-trigger",
        panelId: "bb-groups-panel",
        innerSelector: ".bb-groups__panel-inner",
        iconSelector: ".bb-groups__chevron",
        itemSelector: ".bb-accordion-animate-in"
      });
    }

    function setupLeaderboardAccordion() {
      const trigger = document.getElementById("bb-leaderboard-trigger");
      if (trigger) {
        const prefetchPublicStandings_ = () => {
          if (isLbPublicClosedMode_()) ensurePublicStandingsReady_(false);
        };
        trigger.addEventListener("mouseenter", prefetchPublicStandings_, { passive: true });
        trigger.addEventListener("focusin", prefetchPublicStandings_);
      }

      leaderboardAccordion = initBbAccordion({
        rootId: "bb-leaderboard",
        btnId: "bb-leaderboard-trigger",
        panelId: "bb-leaderboard-panel",
        chevronBtnId: "bb-leaderboard-chevron",
        rootBgFade: true,
        innerSelector: ".bb-leaderboard__panel-inner",
        iconSelector: ".bb-leaderboard__chevron",
        staggerInnerChildren: true,
        onAfterOpen: () => syncLbToolbarVisibility_(),
        onBeforeClose: () => syncLbToolbarVisibility_(),
        onOpen: () => {
          warmLeaderboardService_();
          if (!bracketDatesReady_) return;

          if (isBracketLocked()) {
            if (lbToken && lbStandingsYou) {
              setLbStandingsVisible_(true);
              updateLbChrome_();
              scheduleAccordionRefresh(leaderboardAccordion);
              return;
            }
            if (lbToken) {
              lbRefresh().finally(() => scheduleAccordionRefresh(leaderboardAccordion));
              return;
            }
            showPublicLeaderboardPanel_();
            return;
          }

          if (lbToken && !lbTop100.length) {
            lbRefresh();
          }
        },
        beforeOpen: () => {
          if (!bracketDatesReady_) return false;
          if (isBracketLocked()) return true;
          if (lbToken) return true;
          openLoginModal_("email");
          return false;
        }
      });
    }

    function setupScoringAccordion() {
      scoringAccordion = initBbAccordion({
        rootId: "bb-scoring-section",
        btnId: "bb-scoring-trigger",
        panelId: "bb-scoring-panel",
        rootBgFade: true,
        innerSelector: ".bb-scoring__panel-inner",
        iconSelector: ".bb-scoring__chevron",
        itemSelector: ".bb-accordion-animate-in"
      });
    }

    function setupBoardAccordion() {
      boardAccordion = initBbAccordion({
        rootId: "bb-board-section",
        btnId: "bb-board-trigger",
        panelId: "bb-board-panel",
        rootBgFade: true,
        innerSelector: ".bb-board__panel-inner",
        iconSelector: ".bb-board__chevron",
        staggerInnerChildren: true,
        onAfterOpen: () => {
          stickyBoardTopUpdate();
          scheduleAccordionRefresh(boardAccordion);
        },
        onOpen: () => {
          stickyBoardTopUpdate();
        },
        onClose: () => {
          stickyBoardTopUpdate();
        }
      });
    }

    function openBoardSection() {
      const section = document.getElementById("bb-board-section");
      if (boardAccordion) {
        if (section && section.classList.contains("is-open")) {
          boardAccordion.refreshHeight();
        } else {
          boardAccordion.open();
        }
      } else if (section) {
        section.classList.add("is-open");
        const trigger = document.getElementById("bb-board-trigger");
        if (trigger) trigger.setAttribute("aria-expanded", "true");
        const panel = document.getElementById("bb-board-panel");
        if (panel) panel.style.height = "auto";
      }
    }

    function flushResultsBoardAutoOpen_() {
      if (!isClosedResultsView_()) return;

      const openNow = () => {
        openBoardSection();
        scheduleAccordionRefresh(boardAccordion);
      };

      openNow();
      window.requestAnimationFrame(openNow);
      window.setTimeout(() => scheduleAccordionRefresh(boardAccordion), 800);
    }

    function openLeaderboardSection() {
      const section = document.getElementById("bb-leaderboard");
      if (leaderboardAccordion) {
        if (section && section.classList.contains("is-open")) {
          leaderboardAccordion.refreshHeight();
        } else {
          leaderboardAccordion.open();
        }
      } else if (section) {
        section.classList.add("is-open");
        const trigger = document.getElementById("bb-leaderboard-trigger");
        const chevron = document.getElementById("bb-leaderboard-chevron");
        if (trigger) trigger.setAttribute("aria-expanded", "true");
        if (chevron) chevron.setAttribute("aria-expanded", "true");
        const panel = document.getElementById("bb-leaderboard-panel");
        if (panel) panel.style.height = "auto";
        syncLbToolbarVisibility_();
      }
    }

    function getLeaderboardScrollTop_() {
      const section = document.getElementById("bb-leaderboard");
      if (!section) return 0;
      return getLeaderboardScrollTarget_(section);
    }

    async function waitForAccordionPanelSettled_(panelId) {
      const panel = document.getElementById(panelId);
      const maxMs = 2200;
      const start = Date.now();
      while (Date.now() - start < maxMs) {
        if (typeof gsap !== "undefined" && panel && gsap.isTweening(panel)) {
          await sleepMs_(32);
          continue;
        }
        break;
      }
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
    }

    function animateWindowScrollTo_(targetY, durationMs = 780) {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const clampedTarget = Math.max(0, Math.min(targetY, maxScroll));

      if (prefersReducedMotion_() || Math.abs(window.scrollY - clampedTarget) < 2) {
        window.scrollTo(0, clampedTarget);
        stickyBoardTopUpdate();
        return Promise.resolve();
      }

      if (smoothScrollTo_) {
        smoothScrollTo_(window.scrollY, { instant: true });
      }

      return new Promise((resolve) => {
        const startY = window.scrollY;
        const distance = clampedTarget - startY;
        const startTime = performance.now();
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        function step(now) {
          const progress = Math.min(1, (now - startTime) / durationMs);
          const nextY = startY + distance * easeInOutCubic(progress);
          window.scrollTo(0, nextY);
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            window.scrollTo(0, clampedTarget);
            stickyBoardTopUpdate();
            resolve();
          }
        }

        requestAnimationFrame(step);
      });
    }

    async function scrollLeaderboardSectionToTop_(options = {}) {
      const section = document.getElementById("bb-leaderboard");
      if (!section) return;

      const delayMs = options.delayMs || 0;
      if (delayMs > 0) {
        await sleepMs_(delayMs);
      }

      setLbStandingsVisible_(true);
      updateLbChrome_();

      if (!section.classList.contains("is-open")) {
        openLeaderboardSection();
        await waitForAccordionPanelSettled_("bb-leaderboard-panel");
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      const targetY = getLeaderboardScrollTarget_(section);
      const useInstant = Boolean(options.instant || prefersReducedMotion_());

      if (useInstant) {
        window.scrollTo({ top: targetY, behavior: "instant" });
        stickyBoardTopUpdate();
        return;
      }

      await animateWindowScrollTo_(targetY);
    }

    function scheduleAccordionRefresh(accordion) {
      if (!accordion) return;
      window.requestAnimationFrame(() => accordion.refreshHeight());
    }

    const entryModal = document.getElementById("bb-entry-modal");
    const entryForm = document.getElementById("bb-entry-form");
    const entryMessage = document.getElementById("bb-entry-message");
    const entrySummaryChampion = document.getElementById("bb-entry-summary-champion");
    const entrySummaryPicks = document.getElementById("bb-entry-summary-picks");
    const entrySummaryStatus = document.getElementById("bb-entry-summary-status");

    function updateEntrySummary() {
      const pickedCount = Object.keys(picks).length;
      const champion = picks[CHAMPION_ID] || "Not selected";
      const isComplete = pickedCount === TOTAL_PICKS && Boolean(picks[CHAMPION_ID]);

      entrySummaryChampion.textContent = picks[CHAMPION_ID] ? getFullDisplayTeamName(picks[CHAMPION_ID]) : champion;
      entrySummaryChampion.title = picks[CHAMPION_ID] || "";
      entrySummaryPicks.textContent = `${pickedCount} / ${TOTAL_PICKS}`;
      entrySummaryStatus.textContent = isComplete ? "Ready" : "Incomplete";
    }

    function openEntryModal() {
      if (Object.keys(picks).length !== TOTAL_PICKS || predictionsClosed()) {
        return;
      }

      if (hasAlreadySubmitted()) {
        showToast("You've already entered. Each email can submit one bracket.");
        return;
      }

      updateEntrySummary();
      entryMessage.className = "bb-form-message";
      entryModal.classList.add("is-open");
      entryModal.setAttribute("aria-hidden", "false");
      updateSubmitDock_();

      window.setTimeout(() => {
        document.getElementById("bb-entry-name").focus();
      }, 50);
    }

    function closeEntryModal() {
      entryModal.classList.remove("is-open");
      entryModal.setAttribute("aria-hidden", "true");
      updateSubmitDock_();
    }

    // After a successful submission the submit button becomes a "See
    // leaderboard" button that closes the modal and drops the cursor into the
    // leaderboard email field (pre-filled with the email they entered with).
    function enterLeaderboardMode(button) {
      if (!button) {
        return;
      }
      button.disabled = false;
      button.type = "button";
      button.textContent = "See leaderboard";
      button.onclick = goToLeaderboard;
    }

    function goToLeaderboard() {
      closeEntryModal();
      if (!lbToken) {
        openLoginModal_("email");
        return;
      }
      scrollLeaderboardSectionToTop_();
    }

    function showEntryMessage(message, type = "success") {
      entryMessage.textContent = message;
      entryMessage.className = `bb-form-message is-visible${type === "error" ? " bb-form-message--error" : ""}`;
    }

    function downloadEntryJson(payload) {
      const safeName = payload.full_name
        ? payload.full_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
        : "bracket-entry";

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${safeName || "bracket-entry"}-${Date.now()}.json`;
      document.body.append(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    }

    function buildEntryPayload() {
      const orderedPicks = initialMatches
        .filter((seedMatch) => !seedMatch.display)
        .map((seedMatch) => {
          const liveMatch = getMatch(seedMatch.id);
          return {
            id: seedMatch.id,
            label: seedMatch.label,
            round: roundLabelForSlot(seedMatch.slot),
            teamA: getFullDisplayTeamName(liveMatch.teams[0]),
            teamB: getFullDisplayTeamName(liveMatch.teams[1]),
            winner: picks[seedMatch.id] ? getFullDisplayTeamName(picks[seedMatch.id]) : ""
          };
        });

      return {
        submitted_at: new Date().toISOString(),
        full_name: document.getElementById("bb-entry-name").value.trim(),
        nickname: document.getElementById("bb-entry-nickname").value.trim(),
        email: document.getElementById("bb-entry-email").value.trim(),
        phone: "",
        team_name: document.getElementById("bb-entry-team-name").value.trim(),
        consent: document.getElementById("bb-entry-consent").checked,
        champion: picks[CHAMPION_ID] ? getFullDisplayTeamName(picks[CHAMPION_ID]) : "",
        picks: orderedPicks,
        completed_picks: Object.keys(picks).length,
        total_required_picks: TOTAL_PICKS
      };
    }

    async function handleEntrySubmit(event) {
      event.preventDefault();
      updateEntrySummary();

      if (!isBracketOpen()) {
        showEntryMessage("Predictions haven't opened yet. Please check back on " + formatOpen() + ".", "error");
        return;
      }

      if (isBracketLocked()) {
        showEntryMessage("Predictions are closed. Entries can no longer be submitted.", "error");
        return;
      }

      const pickedCount = Object.keys(picks).length;

      if (pickedCount < TOTAL_PICKS || !picks[CHAMPION_ID]) {
        showEntryMessage("Please complete every bracket pick before submitting your entry.", "error");
        return;
      }

      if (!entryForm.checkValidity()) {
        entryForm.reportValidity();
        return;
      }

      const payload = buildEntryPayload();

      // No backend wired up yet: keep the local JSON backup so nothing is lost.
      if (!isSubmissionEndpointConfigured()) {
        downloadEntryJson(payload);
        showEntryMessage(
          "No submission endpoint is configured yet, so your entry was saved as a downloaded JSON backup. Add your Google Apps Script URL to SUBMISSION_ENDPOINT to enable email + database delivery.",
          "error"
        );
        return;
      }

      const submitButton = entryForm.querySelector('button[type="submit"]');
      const originalLabel = submitButton ? submitButton.textContent : "";
      let succeeded = false;

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = "Checking…";
      }

      // HARD BLOCK: one entry per email. Ask the backend whether this email
      // has already entered before we send the submission.
      try {
        const check = await lbJsonp({ action: "check_email", email: payload.email });
        if (check && check.ok && check.exists) {
          showEntryMessage(
            "This email address has already entered. Each email can submit one bracket, and entries can't be changed once submitted.",
            "error"
          );
          confirmedSubmitted = true;
          rememberSubmission(payload);
          updateEntryButtonsState();
          updateChampionPanel_();
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalLabel;
          }
          return;
        }
      } catch (checkError) {
        // Couldn't reach the check (network/timeout): fall through and submit.
        // The backend still rejects duplicates, so this can't create a double.
      }

      if (submitButton) {
        submitButton.textContent = "Submitting…";
      }

      try {
        // Apps Script web apps don't return CORS headers, so we use a
        // "no-cors" request with a simple text/plain body (no preflight).
        // The response is opaque, so the user's confirmation email is the
        // proof of delivery rather than a readable HTTP status.
        await fetch(SUBMISSION_ENDPOINT, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify(payload)
        });

        rememberSubmission(payload);
        confirmedSubmitted = true;
        succeeded = true;
        updateEntryButtonsState();
        updateChampionPanel_();
        showEntryMessage(
          `Entry submitted! A confirmation email with your full bracket is on its way to ${payload.email}.`
        );
        showToast("Entry submitted.");
        enterLeaderboardMode(submitButton);
      } catch (error) {
        downloadEntryJson(payload);
        showEntryMessage(
          "We couldn't reach the submission server, so your entry was saved as a downloaded JSON backup. Please check your connection and try again.",
          "error"
        );
      } finally {
        // On success the button has been turned into "See leaderboard" — leave
        // it. Only restore the original label if the submission didn't succeed.
        if (submitButton && !succeeded) {
          submitButton.disabled = false;
          submitButton.textContent = originalLabel;
        }
      }
    }

    document.getElementById("bb-open-entry-button").addEventListener("click", openEntryModal);
    document.getElementById("bb-open-entry-button-bottom").addEventListener("click", openEntryModal);
    const submitDockBtn = document.getElementById("bb-submit-dock-btn");
    if (submitDockBtn) submitDockBtn.addEventListener("click", openEntryModal);

    document.querySelectorAll("[data-close-entry-modal]").forEach((button) => {
      button.addEventListener("click", closeEntryModal);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && entryModal.classList.contains("is-open")) {
        closeEntryModal();
      }
    });

    window.addEventListener("resize", () => {
      window.requestAnimationFrame(syncBracketLayout);
    });

    (function wireRoundStepper() {
      const prevBtn = document.getElementById("bb-rounds-prev");
      const nextBtn = document.getElementById("bb-rounds-next");
      if (prevBtn) prevBtn.addEventListener("click", () => stepRounds(-1));
      if (nextBtn) nextBtn.addEventListener("click", () => stepRounds(1));

      // Touch swipe: an additional way to move between rounds on mobile. The
      // Prev/Next buttons keep working exactly as before; this just adds a
      // horizontal finger swipe over the bracket. Vertical scrolling is left
      // untouched — we only act when the gesture is clearly horizontal.
      const wrap = document.getElementById("bb-mirror-wrap");
      if (!wrap) return;

      const SWIPE_MIN = 45; // px of horizontal travel before it counts as a swipe
      let startX = 0;
      let startY = 0;
      let tracking = false;

      wrap.addEventListener("touchstart", (e) => {
        if (e.touches.length !== 1 || roundMaxStep <= 0) {
          tracking = false;
          return;
        }
        tracking = true;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      wrap.addEventListener("touchend", (e) => {
        if (!tracking) return;
        tracking = false;
        const touch = e.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
        // Swipe left reveals the next round; swipe right goes back.
        stepRounds(dx < 0 ? 1 : -1);
      }, { passive: true });
    })();

    // JS-driven sticky for the rounds nav. CSS position: sticky is unreliable
    // on Shopify because a theme wrapper often sets overflow, which silently
    // disables it. This pins the bar to the top of the viewport while the
    // bracket is on screen, regardless of ancestor overflow.
    (function initStickyRoundsNav() {
      const nav = document.getElementById("bb-rounds-nav");
      const wrap = document.getElementById("bb-mirror-wrap");
      if (!nav || !wrap) return;

      const spacer = document.createElement("div");
      spacer.setAttribute("aria-hidden", "true");
      spacer.style.height = "0px";
      nav.parentNode.insertBefore(spacer, nav);

      let fixed = false;

      function unfix() {
        if (!fixed) return;
        fixed = false;
        nav.classList.remove("bb-rounds-nav--fixed");
        nav.style.left = "";
        nav.style.width = "";
        spacer.style.height = "0px";
      }

      function update() {
        if (nav.hidden) { unfix(); return; }

        const navH = nav.offsetHeight;
        const stickyTop = parseFloat(getComputedStyle(nav).getPropertyValue("--bb-rounds-sticky-top")) || 0;
        const anchorTop = spacer.getBoundingClientRect().top;
        const wrapRect = wrap.getBoundingClientRect();
        const shouldFix = anchorTop <= stickyTop && wrapRect.bottom > navH + stickyTop;

        if (shouldFix) {
          const box = spacer.getBoundingClientRect();
          if (!fixed) {
            spacer.style.height = navH + "px";
            fixed = true;
          }
          nav.classList.add("bb-rounds-nav--fixed");
          if (window.innerWidth < 1024) {
            // Mobile: span the full viewport width instead of aligning to the bracket.
            nav.style.left = "0px";
            nav.style.width = "100vw";
          } else {
            nav.style.left = box.left + "px";
            nav.style.width = box.width + "px";
          }
        } else {
          unfix();
        }
      }

      window.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      stickyRoundsNavUpdate = update;
      update();
    })();

    // Desktop: pin "Pick Your Winners" bar (title + countdown + submit) under
    // the theme header once the user scrolls past it.
    (function initStickyBoardTop() {
      const top = document.querySelector(".bb-board__top");
      const board = document.getElementById("bb-board-section");
      if (!top || !board) return;

      const desktopMq = window.matchMedia("(min-width: 1024px)");
      const STICKY_TOP_FALLBACK = 77;

      const spacer = document.createElement("div");
      spacer.className = "bb-board__top-spacer";
      spacer.setAttribute("aria-hidden", "true");
      spacer.style.height = "0px";
      top.parentNode.insertBefore(spacer, top);

      let fixed = false;
      let scrollTicking = false;

      function getStickyTop_() {
        return getPageHeaderOffset_() || STICKY_TOP_FALLBACK;
      }

      function syncStickyTopVar_(px) {
        top.style.setProperty("--bb-board-sticky-top", px + "px");
      }

      function unfix() {
        if (!fixed) return;
        fixed = false;
        top.classList.remove("bb-board__top--fixed");
        top.style.left = "";
        top.style.width = "";
        spacer.style.height = "0px";
      }

      function update() {
        if (!desktopMq.matches) {
          unfix();
          return;
        }
        if (board.classList.contains("bb-board--closed") || board.classList.contains("bb-board--review-active")) {
          unfix();
          return;
        }

        const stickyTop = getStickyTop_();
        syncStickyTopVar_(stickyTop);

        const barH = top.offsetHeight;
        const anchorTop = spacer.getBoundingClientRect().top;
        const boardRect = board.getBoundingClientRect();
        const shouldFix = anchorTop <= stickyTop && boardRect.bottom > barH + stickyTop;

        if (shouldFix) {
          const box = spacer.getBoundingClientRect();
          if (!fixed) {
            spacer.style.height = barH + "px";
            fixed = true;
          }
          top.classList.add("bb-board__top--fixed");
          top.style.left = box.left + "px";
          top.style.width = box.width + "px";
        } else {
          unfix();
        }
      }

      function scheduleUpdate_() {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          scrollTicking = false;
          update();
        });
      }

      function watchThemeHeader_() {
        if (typeof ResizeObserver === "undefined") {
          return;
        }
        const ro = new ResizeObserver(() => scheduleUpdate_());
        document.querySelectorAll(".shopify-section-group-header-group, store-header").forEach((el) => {
          if (!el.closest(".bb-page")) {
            ro.observe(el);
          }
        });
      }

      desktopMq.addEventListener("change", update);
      window.addEventListener("scroll", scheduleUpdate_, { passive: true });
      window.addEventListener("resize", update);
      watchThemeHeader_();
      stickyBoardTopUpdate = update;
      update();
    })();

    (function initSmoothScroll_() {
      const page = document.querySelector(".bb-page");
      if (!page) return;

      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      const desktopMq = window.matchMedia("(min-width: 1024px)");
      const SCROLL_LERP = 0.12;
      const WHEEL_SPEED = 1;

      let wheelTargetY = window.scrollY;
      let wheelRafId = null;

      function getMaxScroll_() {
        return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      }

      function clampScroll_(y) {
        return Math.max(0, Math.min(y, getMaxScroll_()));
      }

      function syncSticky_() {
        stickyBoardTopUpdate();
        stickyRoundsNavUpdate();
      }

      function onScrollEnd_(fn) {
        if ("onscrollend" in window) {
          window.addEventListener("scrollend", fn, { once: true });
          return;
        }

        let timer = null;

        function finish() {
          window.removeEventListener("scroll", tick);
          clearTimeout(timer);
          fn();
        }

        function tick() {
          clearTimeout(timer);
          timer = setTimeout(finish, 120);
        }

        window.addEventListener("scroll", tick, { passive: true });
        timer = setTimeout(finish, 120);
      }

      function cancelWheelAnim_() {
        if (wheelRafId !== null) {
          cancelAnimationFrame(wheelRafId);
          wheelRafId = null;
        }
        wheelTargetY = window.scrollY;
      }

      function syncWheelTarget_() {
        wheelTargetY = clampScroll_(wheelTargetY);
        if (wheelRafId === null) {
          wheelTargetY = window.scrollY;
        }
      }

      function wheelScrollFrame_() {
        wheelTargetY = clampScroll_(wheelTargetY);
        const currentY = window.scrollY;
        const nextY = currentY + (wheelTargetY - currentY) * SCROLL_LERP;

        if (Math.abs(wheelTargetY - currentY) < 0.5) {
          if (currentY !== wheelTargetY) {
            window.scrollTo(0, wheelTargetY);
            syncSticky_();
          }
          wheelRafId = null;
          return;
        }

        window.scrollTo(0, nextY);
        syncSticky_();
        wheelRafId = requestAnimationFrame(wheelScrollFrame_);
      }

      function startWheelLoop_() {
        wheelTargetY = clampScroll_(wheelTargetY);
        if (wheelRafId === null) {
          wheelRafId = requestAnimationFrame(wheelScrollFrame_);
        }
      }

      function getScrollLinkTarget_(link) {
        const scrollId = link.getAttribute("data-bb-scroll");
        if (scrollId) {
          return document.getElementById(scrollId);
        }
        const hash = link.getAttribute("href");
        if (!hash || hash === "#") return null;
        return document.querySelector(hash);
      }

      smoothScrollTo_ = function smoothScrollTo(target, options = {}) {
        cancelWheelAnim_();

        const behavior = reducedMotion.matches || options.instant ? "instant" : "smooth";

        if (typeof target === "number") {
          window.scrollTo({ top: clampScroll_(target), behavior });
          onScrollEnd_(() => {
            syncSticky_();
            wheelTargetY = window.scrollY;
          });
          return;
        }

        const el = typeof target === "string" ? document.querySelector(target) : target;
        if (!el) return;

        stickyBoardTopUpdate();
        const style = getComputedStyle(el);
        const scrollMarginTop = parseFloat(style.scrollMarginTop) || 0;
        const top = el.getBoundingClientRect().top + window.scrollY - scrollMarginTop;
        window.scrollTo({ top: clampScroll_(top), behavior });
        onScrollEnd_(() => {
          syncSticky_();
          wheelTargetY = window.scrollY;
        });
      };

      function canElementScroll_(node, deltaY, deltaX) {
        if (!(node instanceof Element)) return false;

        const style = getComputedStyle(node);
        const vertOverflow = style.overflowY === "auto" || style.overflowY === "scroll";
        const horizOverflow = style.overflowX === "auto" || style.overflowX === "scroll";
        const dy = deltaY || 0;
        const dx = deltaX || 0;

        if (Math.abs(dy) >= Math.abs(dx) && dy !== 0 && vertOverflow) {
          const maxTop = node.scrollHeight - node.clientHeight;
          if (maxTop > 1) {
            if (dy > 0 && node.scrollTop < maxTop - 1) return true;
            if (dy < 0 && node.scrollTop > 0) return true;
          }
        }

        if (Math.abs(dx) > Math.abs(dy) && dx !== 0 && horizOverflow) {
          const maxLeft = node.scrollWidth - node.clientWidth;
          if (maxLeft > 1) {
            if (dx > 0 && node.scrollLeft < maxLeft - 1) return true;
            if (dx < 0 && node.scrollLeft > 0) return true;
          }
        }

        return false;
      }

      function isNestedScrollTarget_(el, deltaY, deltaX) {
        let node = el;
        while (node && node !== document.body && node !== document.documentElement) {
          if (node.classList) {
            if (
              node.classList.contains("bb-groups__panel") ||
              node.classList.contains("bb-leaderboard__panel") ||
              node.classList.contains("bb-scoring__panel") ||
              node.classList.contains("bb-groups__panel-inner") ||
              node.classList.contains("bb-leaderboard__panel-inner") ||
              node.classList.contains("bb-scoring__panel-inner") ||
              node.classList.contains("bb-groups") ||
              node.classList.contains("bb-leaderboard") ||
              node.classList.contains("bb-scoring")
            ) {
              node = node.parentElement;
              continue;
            }
          }

          if (canElementScroll_(node, deltaY, deltaX)) {
            return true;
          }

          node = node.parentElement;
        }
        return false;
      }

      function handleAnchorLinkClick_(event) {
        const link = event.target.closest("[data-bb-scroll], a[href^='#']");
        if (!link || !page.contains(link)) return;

        const target = getScrollLinkTarget_(link);
        if (!target || !page.contains(target)) return;

        event.preventDefault();
        event.stopPropagation();
        if (
          target.id === "bb-board-title" ||
          target.id === "bb-board-trigger" ||
          target.closest("#bb-board-section")
        ) {
          openBoardSection();
        }
        smoothScrollTo_(target);
      }

      page.addEventListener("click", handleAnchorLinkClick_, true);

      window.addEventListener(
        "wheel",
        (event) => {
          if (reducedMotion.matches || !desktopMq.matches) return;
          if (event.ctrlKey || event.metaKey) return;
          if (isNestedScrollTarget_(event.target, event.deltaY, event.deltaX)) return;

          event.preventDefault();
          if (wheelRafId === null) {
            wheelTargetY = window.scrollY;
          }
          wheelTargetY = clampScroll_(wheelTargetY + event.deltaY * WHEEL_SPEED);
          startWheelLoop_();
        },
        { passive: false }
      );

      window.addEventListener(
        "scroll",
        () => {
          if (wheelRafId !== null) return;
          wheelTargetY = window.scrollY;
        },
        { passive: true }
      );

      if (typeof ResizeObserver !== "undefined") {
        const layoutObserver = new ResizeObserver(() => {
          syncWheelTarget_();
        });
        layoutObserver.observe(document.documentElement);
        if (document.body) layoutObserver.observe(document.body);
      }
    })();

    entryForm.addEventListener("submit", handleEntrySubmit);

    function initHeroCarousel() {
      const root = document.querySelector("[data-bb-hero-carousel]");
      if (!root) return;

      const track = document.getElementById("bb-hero-track");
      const slides = [...root.querySelectorAll(".bb-hero__slide")];
      const radios = [...root.querySelectorAll('input[name="bb-hero-slide"]')];
      const prevBtn = root.querySelector(".bb-hero__nav--prev");
      const nextBtn = root.querySelector(".bb-hero__nav--next");
      const progressBar = document.getElementById("bb-hero-progress-bar");
      const playBtn = document.getElementById("bb-hero-playpause");
      const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

      function durationMs() {
        return motionQuery.matches ? 0 : 6500;
      }

      function autoplayDelayMs() {
        return motionQuery.matches ? 9000 : durationMs();
      }

      let index = 0;
      let autoplayTimer = null;
      let isPaused = false;

      function getProgressScale() {
        if (!progressBar) return 0;
        const raw = window.getComputedStyle(progressBar).transform;
        if (!raw || raw === "none") return 0;
        try {
          const m = new DOMMatrixReadOnly(raw);
          return Math.min(1, Math.max(0, m.m11));
        } catch {
          return 0;
        }
      }

      function updatePlayPauseUi() {
        if (!playBtn) return;
        const iconPause = playBtn.querySelector(".bb-hero__playpause-icon--pause");
        const iconPlay = playBtn.querySelector(".bb-hero__playpause-icon--play");
        playBtn.setAttribute("aria-pressed", String(isPaused));
        playBtn.setAttribute(
          "aria-label",
          isPaused ? "Play carousel autoplay" : "Pause carousel autoplay"
        );
        if (iconPause) iconPause.toggleAttribute("hidden", isPaused);
        if (iconPlay) iconPlay.toggleAttribute("hidden", !isPaused);
      }

      function pause() {
        if (isPaused) return;
        isPaused = true;
        clearAutoplay();
        const d = durationMs();
        if (progressBar && d > 0) {
          const scale = getProgressScale();
          progressBar.style.transition = "none";
          progressBar.style.transform = `scaleX(${scale})`;
        }
        updatePlayPauseUi();
      }

      function resume() {
        if (!isPaused) return;
        isPaused = false;
        const d = durationMs();
        if (progressBar && d > 0) {
          const scale = getProgressScale();
          const remaining = Math.max(120, d * (1 - scale));
          progressBar.style.transition = "none";
          progressBar.style.transform = `scaleX(${scale})`;
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              progressBar.style.transition = `transform ${remaining}ms linear`;
              progressBar.style.transform = "scaleX(1)";
            });
          });
          clearAutoplay();
          autoplayTimer = window.setTimeout(() => {
            goTo(index + 1);
          }, remaining);
        } else {
          clearAutoplay();
          scheduleAutoplay();
        }
        updatePlayPauseUi();
      }

      function togglePlayPause() {
        if (isPaused) resume();
        else pause();
      }

      function applyInert() {
        slides.forEach((slide, i) => {
          if (i === index) slide.removeAttribute("inert");
          else slide.setAttribute("inert", "");
        });
      }

      function setActiveClasses() {
        slides.forEach((slide, i) => slide.classList.toggle("is-active", i === index));
      }

      function syncRadios() {
        radios.forEach((radio, i) => {
          radio.checked = i === index;
        });
      }

      function restartProgress() {
        if (!progressBar) return;
        const d = durationMs();
        if (d <= 0) {
          progressBar.style.transition = "none";
          progressBar.style.transform = "scaleX(1)";
          return;
        }
        progressBar.style.transition = "none";
        progressBar.style.transform = "scaleX(0)";
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            progressBar.style.transition = `transform ${d}ms linear`;
            progressBar.style.transform = "scaleX(1)";
          });
        });
      }

      function clearAutoplay() {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
      }

      function scheduleAutoplay() {
        clearAutoplay();
        if (isPaused) return;
        const delay = autoplayDelayMs();
        if (delay <= 0) return;
        autoplayTimer = window.setTimeout(() => {
          goTo(index + 1);
        }, delay);
      }

      function goTo(nextIndex) {
        isPaused = false;
        const n = ((nextIndex % slides.length) + slides.length) % slides.length;
        index = n;
        track.style.transform = `translateX(-${index * 100}%)`;
        setActiveClasses();
        syncRadios();
        applyInert();
        restartProgress();
        scheduleAutoplay();
        updatePlayPauseUi();
      }

      if (playBtn) {
        playBtn.addEventListener("click", (event) => {
          event.stopPropagation();
          togglePlayPause();
        });
      }

      prevBtn.addEventListener("click", () => goTo(index - 1));
      nextBtn.addEventListener("click", () => goTo(index + 1));

      radios.forEach((radio) => {
        radio.addEventListener("change", () => {
          if (!radio.checked) return;
          const i = Number.parseInt(radio.value, 10);
          if (!Number.isNaN(i)) goTo(i);
        });
      });

      root.addEventListener("keydown", (event) => {
        if (event.code === "Space" && event.target === root) {
          event.preventDefault();
          togglePlayPause();
          return;
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          goTo(index - 1);
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          goTo(index + 1);
        }
      });

      document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
          clearAutoplay();
        } else if (!isPaused) {
          restartProgress();
          scheduleAutoplay();
        }
      });

      if (typeof motionQuery.addEventListener === "function") {
        motionQuery.addEventListener("change", () => goTo(index));
      } else if (typeof motionQuery.addListener === "function") {
        motionQuery.addListener(() => goTo(index));
      }

      goTo(0);
    }

    function scheduleDeadlineLock() {
      if (deadlineTimer) {
        window.clearTimeout(deadlineTimer);
        deadlineTimer = null;
      }
      if (isBracketLocked()) {
        return;
      }

      const msUntilLock = SUBMISSION_DEADLINE.getTime() - Date.now();

      // setTimeout caps at ~24.8 days; only schedule when it's safely in range.
      if (msUntilLock > 0 && msUntilLock < 2147483647) {
        deadlineTimer = window.setTimeout(() => {
          closeEntryModal();
          applyActualResults();
          applyLockUi();
          renderBracket();
          flushResultsBoardAutoOpen_();
          showToast("Predictions are now closed.");
        }, msUntilLock);
      }
    }

    function scheduleOpenUnlock() {
      if (openTimer) {
        window.clearTimeout(openTimer);
        openTimer = null;
      }
      if (isBracketOpen()) {
        return;
      }

      const msUntilOpen = SUBMISSION_OPEN.getTime() - Date.now();

      // setTimeout caps at ~24.8 days; only schedule when it's safely in range.
      if (msUntilOpen > 0 && msUntilOpen < 2147483647) {
        openTimer = window.setTimeout(() => {
          renderBracket();
          showToast("Predictions are now open!");
        }, msUntilOpen);
      }
    }

    // Live countdown chip beside the progress pill. Preview → opens in; Open → closes in.
    function formatCountdown(ms) {
      if (ms <= 0) {
        return "0s";
      }
      let s = Math.floor(ms / 1000);
      const d = Math.floor(s / 86400); s -= d * 86400;
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60); s -= m * 60;
      const parts = [];
      if (d) parts.push(d + "d");
      if (d || h) parts.push(h + "h");
      if (d || h || m) parts.push(m + "m");
      parts.push(s + "s");
      return parts.join(" ");
    }

    function rgbToHex_(r, g, b) {
      return "#" + [r, g, b].map(function (v) {
        return Math.round(v).toString(16).padStart(2, "0");
      }).join("");
    }

    function lerpRgb_(a, b, t) {
      return {
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t
      };
    }

    // Close-state accent: green while plenty of time, then orange, then red near deadline.
    function countdownCloseAccent_(pctRemaining) {
      const green = { r: 21, g: 246, b: 166 };
      const orange = { r: 255, g: 149, b: 0 };
      const red = { r: 255, g: 0, b: 87 };
      const pct = Math.max(0, Math.min(1, pctRemaining));

      if (pct >= 0.5) {
        return "#15f6a6";
      }
      if (pct >= 0.15) {
        const t = (0.5 - pct) / 0.35;
        const c = lerpRgb_(green, orange, t);
        return rgbToHex_(c.r, c.g, c.b);
      }
      const t = (0.15 - pct) / 0.15;
      const c = lerpRgb_(orange, red, t);
      return rgbToHex_(c.r, c.g, c.b);
    }

    function setCountdownAccent_(el, color) {
      el.style.setProperty("--bb-countdown-accent", color);
    }

    function updateCountdown() {
      const el = document.getElementById("bb-tray-unified");
      const clock = document.getElementById("bb-countdown-clock");
      const label = document.getElementById("bb-countdown-label");
      if (!el || !clock) {
        return;
      }

      if (!bracketDatesReady_) {
        el.classList.remove("bb-tray-unified--no-countdown", "bb-tray-unified--opens", "bb-tray-unified--closes");
        setCountdownAccent_(el, "#15f6a6");
        if (label) {
          label.textContent = "Closes in";
        }
        clock.textContent = "—";
        return;
      }

      if (isBracketLocked()) {
        el.classList.add("bb-tray-unified--no-countdown");
        return;
      }

      el.classList.remove("bb-tray-unified--no-countdown");

      if (!isBracketOpen()) {
        el.classList.add("bb-tray-unified--opens");
        el.classList.remove("bb-tray-unified--closes");
        setCountdownAccent_(el, "#15f6a6");
        if (label) {
          label.textContent = "Opens in";
        }
        clock.textContent = formatCountdown(SUBMISSION_OPEN.getTime() - Date.now());
        return;
      }

      el.classList.add("bb-tray-unified--closes");
      el.classList.remove("bb-tray-unified--opens");
      if (label) {
        label.textContent = "Closes in";
      }
      const remaining = SUBMISSION_DEADLINE.getTime() - Date.now();
      const windowMs = SUBMISSION_DEADLINE.getTime() - SUBMISSION_OPEN.getTime();
      const pctRemaining = windowMs > 0 ? remaining / windowMs : 0;
      setCountdownAccent_(el, countdownCloseAccent_(pctRemaining));
      clock.textContent = formatCountdown(remaining);
    }

    function startCountdown() {
      const fill = document.getElementById("bb-progress-fill");
      if (fill && typeof gsap !== "undefined" && !prefersReducedMotion_()) {
        gsap.set(fill, { scaleX: 0 });
      }
      updateCountdown();
      if (countdownTimer) {
        window.clearInterval(countdownTimer);
      }
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }

    // ===== Leaderboard (login-gated) ==================================
    const LB_ROUNDS = [
      { key: "r32", label: "R32" },
      { key: "r16", label: "R16" },
      { key: "qf", label: "QF" },
      { key: "sf", label: "SF" },
      { key: "final", label: "Final" }
    ];

    const LB_SESSION_KEY = "bbLbSession";
    const LB_SESSION_MS = 7 * 24 * 60 * 60 * 1000;

    let lbToken = null;
    let lbEmail = "";
    let lbStandingsYou = null;
    let lbTop100 = [];
    let lbTop100Source_ = [];
    let lbSearchQuery_ = "";
    let lbSearchActive_ = false;
    let lbPage = 0;
    let lbPageAnimating = false;
    let lbEmptyMessage_ = "";
    let lbSearchTimer = null;
    let lbSearchFetchSeq_ = 0;
    let lbPublicPreloadPromise_ = null;
    let lbShouldAutoOpen_ = false;
    const LB_PAGE_SIZE = 10;

    const loginModal = document.getElementById("bb-login-modal");
    const loginModalDialog = loginModal ? loginModal.querySelector(".bb-modal__dialog") : null;
    const loginModalBackdrop = loginModal ? loginModal.querySelector(".bb-modal__backdrop") : null;

    function maskEmail_(email) {
      const value = String(email || "");
      const at = value.indexOf("@");
      if (at < 2) return value;
      return value.slice(0, 2) + "…" + value.slice(at);
    }

    function saveLbSession_(token, email) {
      lbToken = token || null;
      lbEmail = email || "";
      try {
        window.localStorage.setItem(
          LB_SESSION_KEY,
          JSON.stringify({
            token: lbToken,
            email: lbEmail,
            expiresAt: Date.now() + LB_SESSION_MS
          })
        );
      } catch {
        /* ignore */
      }
      updateLeaderboardTriggerCopy_();
      updateChampionPanel_();
    }

    function loadLbSession_() {
      try {
        const raw = window.localStorage.getItem(LB_SESSION_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || !data.token || !data.expiresAt || Date.now() > Number(data.expiresAt)) {
          clearLbSession_(false);
          return false;
        }
        lbToken = data.token;
        lbEmail = data.email || "";
        return true;
      } catch {
        return false;
      }
    }

    function clearLbSession_(updateUi = true) {
      lbToken = null;
      lbEmail = "";
      lbStandingsYou = null;
      lbTop100 = [];
      lbTop100Source_ = [];
      lbSearchQuery_ = "";
      lbSearchActive_ = false;
      lbPage = 0;
      try {
        window.localStorage.removeItem(LB_SESSION_KEY);
      } catch {
        /* ignore */
      }
      if (lbEl("bb-lb-email")) lbEl("bb-lb-email").value = "";
      const searchInput = lbEl("bb-lb-search-email");
      if (searchInput) searchInput.value = "";
      updateLbSearchClearBtn_();
      const searchMsg = lbEl("bb-lb-search-message");
      if (searchMsg) {
        searchMsg.hidden = true;
        searchMsg.textContent = "";
        searchMsg.classList.remove("is-error");
      }
      lbEmptyMessage_ = "";
      clearLbOtp();
      clearLbMessage();
      showLbModalStep_("email");
      setLbStandingsVisible_(false);
      if (updateUi) {
        updateLeaderboardTriggerCopy_();
        updateChampionPanel_();
        if (isLbPublicClosedMode_()) {
          window.requestAnimationFrame(() => flushPublicLeaderboardAutoOpen_());
        } else if (leaderboardAccordion) {
          leaderboardAccordion.close();
        }
        if (isBracketLocked()) {
          preloadPublicStandingsWithRetry_();
        }
      }
    }

    function isLbPublicClosedMode_() {
      return !lbToken && isBracketLocked();
    }

    function showPublicLeaderboardPanel_() {
      setLbStandingsVisible_(true);
      updateLbChrome_();

      if (lbTop100Source_.length) {
        lbEmptyMessage_ = "";
        renderLbTablePage_(null, { animate: false });
        scheduleAccordionRefresh(leaderboardAccordion);
        ensurePublicStandingsReady_(false);
        return;
      }

      lbEmptyMessage_ = "Loading standings…";
      renderLbTablePage_(null, { animate: false });
      scheduleAccordionRefresh(leaderboardAccordion);
      ensurePublicStandingsReady_(true).then(() => {
        lbEmptyMessage_ = lbTop100Source_.length ? "" : "Standings could not be loaded. Try again in a moment.";
        renderLbTablePage_(null, { animate: false });
        scheduleAccordionRefresh(leaderboardAccordion);
      });
    }

    function syncMyBracketButton_() {
      const mb = lbEl("bb-lb-mybracket-btn");
      if (!mb) return;
      mb.hidden = !(lbToken && lastYouPicks);
    }

    let lbToolbarAnimating_ = false;

    function syncLbToolbarVisibility_(instant) {
      try {
        const toolbar = lbEl("bb-lb-toolbar");
        const root = document.getElementById("bb-leaderboard");
        const standingsStep = lbEl("bb-lb-standings-step");
        const standingsVisible = standingsStep && !standingsStep.hidden;
        const eligible = Boolean(lbToken) && standingsVisible;
        const open = Boolean(root && root.classList.contains("is-open"));
        const shouldShow = eligible && open;

        syncMyBracketButton_();
        if (!toolbar) return;

        if (!eligible) {
          if (typeof gsap !== "undefined") gsap.killTweensOf(toolbar);
          lbToolbarAnimating_ = false;
          toolbar.hidden = true;
          toolbar.style.opacity = "";
          toolbar.style.transform = "";
          return;
        }

        if (shouldShow && !toolbar.hidden && !lbToolbarAnimating_) return;
        if (!shouldShow && toolbar.hidden && !lbToolbarAnimating_) return;

        if (instant || prefersReducedMotion_() || typeof gsap === "undefined") {
          if (typeof gsap !== "undefined") gsap.killTweensOf(toolbar);
          lbToolbarAnimating_ = false;
          toolbar.hidden = !shouldShow;
          toolbar.style.opacity = shouldShow ? "1" : "0";
          toolbar.style.transform = shouldShow ? "none" : "translateY(-6px)";
          return;
        }

        gsap.killTweensOf(toolbar);
        lbToolbarAnimating_ = true;

        if (shouldShow) {
          toolbar.hidden = false;
          gsap.fromTo(
            toolbar,
            { opacity: 0, y: -6, scale: 0.96 },
            {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.35,
              ease: "back.out(1.4)",
              delay: 0.08,
              onComplete: () => {
                lbToolbarAnimating_ = false;
              }
            }
          );
          return;
        }

        gsap.to(toolbar, {
          opacity: 0,
          y: -6,
          scale: 0.96,
          duration: 0.4,
          ease: "power2.in",
          onComplete: () => {
            toolbar.hidden = true;
            toolbar.style.opacity = "";
            toolbar.style.transform = "";
            lbToolbarAnimating_ = false;
          }
        });
      } catch (err) {
        console.error(err);
        lbToolbarAnimating_ = false;
      }
    }

    function updateLbChrome_() {
      const searchWrap = lbEl("bb-lb-search-wrap");
      const standingsStep = lbEl("bb-lb-standings-step");
      const standingsVisible = standingsStep && !standingsStep.hidden;
      const isLoggedIn = Boolean(lbToken);
      const isPublic = isLbPublicClosedMode_();

      syncLbToolbarVisibility_();
      if (searchWrap) searchWrap.hidden = !isPublic || !standingsVisible;

      const youCard = lbEl("bb-lb-you");
      if (youCard && !isLoggedIn) youCard.hidden = true;
    }

    function updateLeaderboardTriggerCopy_() {
      const title = lbEl("bb-leaderboard-title");
      const copy = lbEl("bb-leaderboard-copy");
      if (title && copy) {
        title.textContent = "The Leaderboard";
        if (lbToken) {
          copy.textContent = "Your live scores and the top 100. Tap to expand.";
        } else if (isLbPublicClosedMode_()) {
          copy.textContent = "Top 100 standings are live. Search by name, email, nickname, or team to find your entry.";
        } else {
          copy.textContent = "Log in to see the top 100 and your score. Scores update automatically as results come in.";
        }
      }
      updateHeroCta_();
    }

    function initHeroCta_() {
      const cta = document.getElementById("bb-hero-cta");
      if (!cta || cta.dataset.bbBound) return;
      cta.dataset.bbBound = "1";
      cta.addEventListener("click", (event) => {
        if (lbToken || !isBracketLocked()) return;
        event.preventDefault();
        event.stopPropagation();
        openLoginModal_("email");
      });
    }

    function updateHeroCta_() {
      const cta = document.getElementById("bb-hero-cta");
      if (!cta) return;
      if (lbToken) {
        cta.hidden = true;
        return;
      }
      cta.hidden = false;
      if (isBracketLocked()) {
        cta.textContent = "Log In";
        cta.removeAttribute("data-bb-scroll");
      } else {
        cta.textContent = "Play for free";
        cta.setAttribute("data-bb-scroll", "bb-board-title");
      }
    }

    function updateChampionPanel_() {
      const root = lbEl("bb-champion-root");
      const label = lbEl("bb-champion-label");
      const empty = lbEl("bb-champion-empty");
      const entered = lbEl("bb-champion-entered");
      const message = lbEl("bb-champion-message");
      const value = lbEl("bb-champion-display");
      const kicker = lbEl("bb-champion-kicker");
      const meta = lbEl("bb-champion-meta");
      const viewScoreBtn = lbEl("bb-champion-view-score-btn");
      const selectBtn = lbEl("bb-champion-select-btn");
      const actions = lbEl("bb-champion-actions");
      if (!label || !value) return;

      const isLoggedIn = Boolean(lbToken);
      const hasSessionChampion = Boolean(picks[CHAMPION_ID]);
      const submitted = hasAlreadySubmitted();

      if (root) {
        root.classList.remove("is-champion-entered", "is-champion-empty", "is-champion-closed");
      }
      resetChampionEmptyPresentation_();
      if (empty) empty.hidden = true;
      if (entered) entered.hidden = true;
      if (message) {
        message.hidden = true;
        message.textContent = "";
      }
      if (kicker) kicker.hidden = true;
      if (meta) meta.hidden = true;
      value.hidden = true;
      if (actions) actions.hidden = true;
      if (selectBtn) selectBtn.hidden = false;

      if (isLoggedIn && lbStandingsYou) {
        label.textContent = "Your champion";
        value.hidden = false;
        const champTeam = lbStandingsYou.picks && lbStandingsYou.picks[CHAMPION_ID];
        if (champTeam) {
          setChampionDisplayWithFlag(value, champTeam);
        } else {
          clearHeroChampionDisplay(value, "Not selected");
        }
        if (meta) {
          meta.hidden = false;
          lbEl("bb-champion-rank").textContent = "#" + lbStandingsYou.rank;
          lbEl("bb-champion-pts").textContent = Number(lbStandingsYou.total || 0) + " pts";
          lbEl("bb-champion-email").textContent = "Logged in as " + maskEmail_(lbEmail);
        }
        return;
      }

      if (submitted) {
        label.textContent = "You're in the game";
        if (entered) entered.hidden = false;
        if (root) root.classList.add("is-champion-entered");
        return;
      }

      if (hasSessionChampion) {
        label.textContent = "Your champion";
        value.hidden = false;
        setChampionDisplayWithFlag(value, picks[CHAMPION_ID]);
        if (kicker) kicker.hidden = false;
        if (actions) actions.hidden = false;
        configureChampionActionButtons_(selectBtn);
        if (!isChampionClosedLoggedOut_() && selectBtn) selectBtn.hidden = true;
        return;
      }

      label.textContent = "Your champion";
      if (empty) empty.hidden = false;
      if (actions) actions.hidden = false;
      if (root) root.classList.add("is-champion-empty");
      if (isChampionClosedLoggedOut_()) {
        applyChampionClosedLoggedOutUi_(selectBtn);
      } else {
        configureChampionActionButtons_(selectBtn);
      }
    }

    function isChampionClosedLoggedOut_() {
      return isBracketLocked() && !lbToken;
    }

    function getChampionEmptyRefs_() {
      const empty = lbEl("bb-champion-empty");
      const headline =
        lbEl("bb-champion-headline") || (empty ? empty.querySelector(".bb-champion__headline") : null);
      const headlineText =
        lbEl("bb-champion-headline-text") ||
        (headline ? headline.querySelector("#bb-champion-headline-text, :scope > span:not(.bb-champion__pending-dot)") : null);
      const emptyBody =
        lbEl("bb-champion-empty-body") || (empty ? empty.querySelector(".bb-champion__body") : null);
      return {
        headline,
        headlineText,
        emptyBody,
        root: lbEl("bb-champion-root")
      };
    }

    function setChampionHeadlineText_(headlineText, text) {
      if (!headlineText) return;
      headlineText.textContent = text;
    }

    function resetChampionEmptyPresentation_() {
      const { headline, headlineText, emptyBody, root } = getChampionEmptyRefs_();
      if (headline) {
        headline.classList.add("bb-champion__headline--pending");
        headline.classList.remove("bb-champion__headline--closed");
      }
      setChampionHeadlineText_(headlineText, "Pending");
      if (emptyBody) {
        emptyBody.classList.remove("bb-champion__body--closed");
        emptyBody.textContent = "Your champion gets locked in after you complete your bracket.";
      }
      if (root) root.classList.remove("is-champion-closed");
    }

    function applyChampionClosedLoggedOutUi_(selectBtn) {
      const { headline, headlineText, emptyBody, root } = getChampionEmptyRefs_();
      if (headline) {
        headline.classList.remove("bb-champion__headline--pending");
        headline.classList.add("bb-champion__headline--closed");
      }
      setChampionHeadlineText_(headlineText, "Closed");
      if (emptyBody) {
        emptyBody.classList.add("bb-champion__body--closed");
        emptyBody.textContent =
          "Bracket is now closed (locked on " + formatDeadline() + "). Log in and/or follow along in the Results bracket below.";
      }
      if (root) root.classList.add("is-champion-closed");
      if (selectBtn) {
        selectBtn.hidden = false;
        selectBtn.textContent = "See Leaderboard";
        selectBtn.removeAttribute("data-bb-scroll");
      }
    }

    function configureChampionActionButtons_(selectBtn) {
      if (!selectBtn) return;
      if (isChampionClosedLoggedOut_()) {
        selectBtn.hidden = false;
        selectBtn.textContent = "See Leaderboard";
        selectBtn.removeAttribute("data-bb-scroll");
      } else {
        selectBtn.textContent = "Start Bracket";
        selectBtn.setAttribute("data-bb-scroll", "bb-board-title");
      }
    }

    function initChampionSelectBtn_() {
      const selectBtn = lbEl("bb-champion-select-btn");
      if (!selectBtn || selectBtn.dataset.bbBound) return;
      selectBtn.dataset.bbBound = "1";
      selectBtn.addEventListener("click", (event) => {
        if (selectBtn.textContent.trim().toLowerCase() !== "see leaderboard") return;
        event.preventDefault();
        event.stopPropagation();
        scrollLeaderboardSectionToTop_();
      });
    }

    function prefillLoginEmail_() {
      const input = lbEl("bb-lb-email");
      if (!input || input.value) return;
      const stored = getStoredEntry();
      if (stored && stored.email) input.value = stored.email;
    }

    function openLoginModal_(step) {
      if (!loginModal) return;
      prefillLoginEmail_();
      showLbModalStep_(step || "email");
      clearLbMessage();
      loginModal.classList.add("is-open");
      loginModal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";

      if (prefersReducedMotion_() || typeof gsap === "undefined" || !loginModalDialog || !loginModalBackdrop) {
        if (loginModalBackdrop) loginModalBackdrop.style.opacity = "1";
        if (loginModalDialog) {
          loginModalDialog.style.opacity = "1";
          loginModalDialog.style.transform = "none";
        }
        window.setTimeout(() => {
          const input = lbEl("bb-lb-email");
          if (input && !input.closest("[hidden]")) input.focus({ preventScroll: true });
        }, 50);
        return;
      }

      gsap.set(loginModalBackdrop, { opacity: 0 });
      gsap.set(loginModalDialog, { opacity: 0, y: 24, scale: 0.97 });
      gsap
        .timeline({
          onComplete: () => {
            const input = lbEl("bb-lb-email");
            if (step === "code") focusLbOtp(0);
            else if (input && !input.closest("[hidden]")) input.focus({ preventScroll: true });
          }
        })
        .to(loginModalBackdrop, { opacity: 1, duration: 0.28, ease: "power2.out" }, 0)
        .to(loginModalDialog, { opacity: 1, y: 0, scale: 1, duration: 0.42, ease: "power3.out" }, 0.06);
    }

    function closeLoginModal_() {
      if (!loginModal || !loginModal.classList.contains("is-open")) return;

      const finish = () => {
        loginModal.classList.remove("is-open");
        loginModal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
      };

      if (prefersReducedMotion_() || typeof gsap === "undefined" || !loginModalDialog || !loginModalBackdrop) {
        finish();
        return;
      }

      gsap
        .timeline({ onComplete: finish })
        .to(loginModalDialog, { opacity: 0, y: 16, scale: 0.98, duration: 0.22, ease: "power2.in" }, 0)
        .to(loginModalBackdrop, { opacity: 0, duration: 0.2, ease: "power2.in" }, 0.04);
    }

    function initLoginModal_() {
      if (!loginModal) return;

      loginModal.querySelectorAll("[data-close-login-modal]").forEach((el) => {
        el.addEventListener("click", closeLoginModal_);
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && loginModal.classList.contains("is-open")) {
          closeLoginModal_();
        }
      });

      const viewScoreBtn = lbEl("bb-champion-view-score-btn");
      if (viewScoreBtn) viewScoreBtn.addEventListener("click", () => openLoginModal_("email"));

      const championLoginBtn = lbEl("bb-champion-login-btn");
      if (championLoginBtn) championLoginBtn.addEventListener("click", () => openLoginModal_("email"));

      initChampionSelectBtn_();

      const panelLogout = lbEl("bb-champion-logout-btn");
      if (panelLogout) panelLogout.addEventListener("click", lbLogout);
    }

    async function restoreLbSession_() {
      if (!loadLbSession_()) {
        updateLeaderboardTriggerCopy_();
        updateChampionPanel_();
        return;
      }
      updateLeaderboardTriggerCopy_();
      try {
        const res = await lbJsonp({ action: "standings", token: lbToken });
        if (res && res.ok) {
          renderStandings(res, { autoOpen: true });
        } else {
          clearLbSession_(false);
          if (isBracketLocked()) {
            await preloadPublicStandingsWithRetry_();
            updateLeaderboardTriggerCopy_();
            updateChampionPanel_();
          } else {
            clearLbSession_();
          }
        }
      } catch {
        clearLbSession_(false);
        if (isBracketLocked()) {
          await preloadPublicStandingsWithRetry_();
        }
        updateLeaderboardTriggerCopy_();
        updateChampionPanel_();
      }
    }

    // My Bracket (review) state.
    let reviewMode = false;
    let lastYouPicks = null;
    let savedPicks = null;
    let panelOriginParent = null;
    let panelOriginNext = null;
    let panelPlaceholder = null;

    function lbEl(id) {
      return document.getElementById(id);
    }

    // Rebuild the global `matches` tree by advancing a set of picks through the
    // bracket (R32 winners feed R16, etc.). Used for both restoring the live
    // board and constructing the read-only "My Bracket" view.
    function rebuildMatchesFromPicks(pickMap) {
      matches = structuredClone(initialMatches);
      initialMatches
        .filter((m) => !m.display)
        .forEach((seed) => {
          const winner = pickMap[seed.id];
          if (!winner) return;
          const live = getMatch(seed.id);
          if (live && live.nextMatch) {
            const nm = getMatch(live.nextMatch);
            if (nm) nm.teams[live.nextSlot] = winner;
          }
        });
    }

    function enterMyBracket() {
      if (!lastYouPicks) return;
      const section = document.getElementById("bb-board-section");
      const panel = document.getElementById("bb-board-panel");
      const slot = lbEl("bb-mybracket-slot");
      const host = lbEl("bb-mybracket-host");
      const tableWrap = document.querySelector(".bb-lb-table-wrap");
      if (!section || !panel || !slot || !host) return;

      savedPicks = { ...picks };
      panelOriginParent = panel.parentNode;
      panelOriginNext = panel.nextSibling;

      // Leave a static copy in the results accordion while reviewing picks.
      panelPlaceholder = panel.cloneNode(true);
      panelPlaceholder.removeAttribute("id");
      panelPlaceholder.classList.add("bb-board__panel--placeholder");
      panelPlaceholder.setAttribute("aria-hidden", "true");
      panelPlaceholder.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
      panelOriginParent.insertBefore(panelPlaceholder, panel);
      if (boardAccordion && boardAccordion.setPanelNode) {
        boardAccordion.setPanelNode(panelPlaceholder);
        if (section.classList.contains("is-open")) {
          scheduleAccordionRefresh(boardAccordion);
        } else {
          panelPlaceholder.style.height = "0px";
        }
      }

      picks = {};
      Object.keys(lastYouPicks).forEach((id) => { picks[id] = lastYouPicks[id]; });
      rebuildMatchesFromPicks(picks);

      reviewMode = true;
      section.classList.add("bb-board--review-active");
      panel.classList.add("bb-board__panel--review");
      slot.appendChild(panel);

      host.hidden = false;
      if (tableWrap) tableWrap.hidden = true;
      const pag = lbEl("bb-lb-pagination");
      if (pag) pag.hidden = true;
      const btn = lbEl("bb-lb-mybracket-btn");
      if (btn) btn.textContent = "View Leaderboard";

      renderBracket();
      applyLockUi();
      scheduleAccordionRefresh(leaderboardAccordion);
      scheduleAccordionRefresh(boardAccordion);
    }

    function exitMyBracket() {
      const section = document.getElementById("bb-board-section");
      const panel = document.getElementById("bb-board-panel");
      const wasOpen = section && section.classList.contains("is-open");
      if (panel) {
        panel.classList.remove("bb-board__panel--review");
        if (panelPlaceholder && panelPlaceholder.parentNode) {
          panelPlaceholder.parentNode.insertBefore(panel, panelPlaceholder);
          panelPlaceholder.remove();
        } else if (panelOriginParent) {
          panelOriginParent.insertBefore(panel, panelOriginNext);
        }
        if (boardAccordion && boardAccordion.setPanelNode) {
          boardAccordion.setPanelNode(panel);
        }
        if (!wasOpen) {
          panel.style.height = "0px";
        }
      }
      if (section) section.classList.remove("bb-board--review-active");
      panelPlaceholder = null;
      panelOriginParent = null;
      panelOriginNext = null;
      reviewMode = false;

      if (savedPicks) {
        picks = savedPicks;
        savedPicks = null;
      }
      rebuildMatchesFromPicks(picks);

      const host = lbEl("bb-mybracket-host");
      const tableWrap = document.querySelector(".bb-lb-table-wrap");
      if (host) host.hidden = true;
      if (tableWrap) tableWrap.hidden = false;
      updateLbPagination_();
      const btn = lbEl("bb-lb-mybracket-btn");
      if (btn) btn.textContent = "My Bracket";

      renderBracket();
      applyLockUi();
      scheduleAccordionRefresh(leaderboardAccordion);
      scheduleAccordionRefresh(boardAccordion);
    }

    function toggleMyBracket() {
      if (reviewMode) exitMyBracket();
      else enterMyBracket();
    }

    // Leaderboard reads use fetch (CORS-enabled on the Apps Script web app).
    // JSONP script tags are unreliable on Shopify themes and often fail immediately.
    const LB_API_TIMEOUT_MS = 90000;

    function lbServiceErrorMessage(err) {
      if (err && err.message === "timeout") {
        return "The server is still waking up — this can take up to a minute the first time. Please try again and wait on the button.";
      }
      if (err && err.message === "network") {
        return "We couldn't connect to check your entry. Please check your connection and try again.";
      }
      return "We couldn't connect to check your entry right now. Please try again in a moment.";
    }

    let leaderboardServiceWarmed = false;

    function warmLeaderboardService_() {
      if (leaderboardServiceWarmed || !isSubmissionEndpointConfigured()) return;
      leaderboardServiceWarmed = true;
      lbJsonp({ action: "ping" }).catch(() => {});
    }

    function lbJsonp(params) {
      return new Promise((resolve, reject) => {
        if (!isSubmissionEndpointConfigured()) {
          reject(new Error("no_endpoint"));
          return;
        }

        const qs = Object.keys(params)
          .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
          .join("&");

        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = window.setTimeout(() => {
          if (controller) controller.abort();
        }, LB_API_TIMEOUT_MS);

        fetch(SUBMISSION_ENDPOINT + "?" + qs, {
          method: "GET",
          redirect: "follow",
          signal: controller ? controller.signal : undefined
        })
          .then((res) => {
            if (!res.ok) throw new Error("network");
            return res.text();
          })
          .then((text) => {
            const trimmed = String(text || "").trim();
            try {
              resolve(JSON.parse(trimmed));
              return;
            } catch (parseErr) {
              const match = trimmed.match(/^[^(]+\(([\s\S]*)\);?\s*$/);
              if (match) {
                resolve(JSON.parse(match[1]));
                return;
              }
              throw new Error("network");
            }
          })
          .catch((err) => {
            if (err && err.name === "AbortError") reject(new Error("timeout"));
            else reject(new Error(err && err.message === "network" ? "network" : "network"));
          })
          .finally(() => window.clearTimeout(timer));
      });
    }

    function showLbMessage(message, type = "success") {
      const el = lbEl("bb-lb-message");
      if (!el) return;
      el.textContent = message;
      el.className = "bb-form-message is-visible" + (type === "error" ? " bb-form-message--error" : "");
      el.style.opacity = "1";
      el.style.transform = "none";
    }

    function clearLbMessage() {
      const el = lbEl("bb-lb-message");
      if (!el) return;
      el.textContent = "";
      el.className = "bb-form-message";
      el.style.opacity = "";
      el.style.transform = "";
    }

    function showLbModalStep_(step) {
      ["email", "code"].forEach((name) => {
        const node = lbEl("bb-lb-" + name + "-step");
        if (node) node.hidden = name !== step;
      });
    }

    function setLbStandingsVisible_(visible) {
      const node = lbEl("bb-lb-standings-step");
      if (node) node.hidden = !visible;
      updateLbChrome_();
      scheduleAccordionRefresh(leaderboardAccordion);
    }

    function formatLbUpdated(iso) {
      if (!iso) return "Scores haven't been calculated yet.";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      try {
        return "Last updated: " + new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(d);
      } catch {
        return "Last updated: " + d.toLocaleString();
      }
    }

    function lbEscapeHtml(str) {
      return String(str == null ? "" : str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    function roundsChipsHtml(person) {
      return LB_ROUNDS
        .map((r) => `<span class="bb-lb-chip">${r.label} <strong>${Number(person[r.key] || 0)}</strong></span>`)
        .join("");
    }

    function renderYouCard(you) {
      const card = lbEl("bb-lb-you");
      if (!card) return;
      if (!lbToken || !you) {
        card.hidden = true;
        return;
      }
      card.hidden = false;
      lbEl("bb-lb-you-rank").textContent = "#" + you.rank;
      lbEl("bb-lb-you-name").textContent = you.full_name || "You";
      lbEl("bb-lb-you-total").textContent = Number(you.total || 0);
      lbEl("bb-lb-you-rounds").innerHTML = roundsChipsHtml(you);
    }

    function getLbPageCount_() {
      return Math.max(1, Math.ceil(lbTop100.length / LB_PAGE_SIZE));
    }

    function updateLbPagination_() {
      const pag = lbEl("bb-lb-pagination");
      const prev = lbEl("bb-lb-prev-btn");
      const next = lbEl("bb-lb-next-btn");
      const label = lbEl("bb-lb-page-label");
      if (!pag) return;

      if (lbSearchActive_ || !lbTop100.length || reviewMode) {
        pag.hidden = true;
        return;
      }

      pag.hidden = getLbPageCount_() <= 1;
      const start = lbPage * LB_PAGE_SIZE + 1;
      const end = Math.min((lbPage + 1) * LB_PAGE_SIZE, lbTop100.length);
      if (label) label.textContent = start + "–" + end + " of " + lbTop100.length;
      if (prev) prev.disabled = lbPageAnimating || lbPage <= 0;
      if (next) next.disabled = lbPageAnimating || lbPage >= getLbPageCount_() - 1;
    }

    function appendLbDataRow_(tbody, p, you) {
      const youRank = you ? you.rank : null;
      const isYou = youRank && p.rank === youRank && you.full_name === p.full_name;
      const row = document.createElement("tr");
      row.className = "bb-lb-row" + (isYou ? " is-you" : "");
      row.innerHTML =
        `<td class="bb-lb-rank">${p.rank}</td>` +
        `<td class="bb-lb-name">${lbEscapeHtml(p.full_name)}<span class="bb-lb-caret">›</span></td>` +
        `<td class="bb-lb-round">${Number(p.r32 || 0)}</td>` +
        `<td class="bb-lb-round">${Number(p.r16 || 0)}</td>` +
        `<td class="bb-lb-round">${Number(p.qf || 0)}</td>` +
        `<td class="bb-lb-round">${Number(p.sf || 0)}</td>` +
        `<td class="bb-lb-round">${Number(p.final || 0)}</td>` +
        `<td class="bb-lb-total">${Number(p.total || 0)}</td>`;

      const detail = document.createElement("tr");
      detail.className = "bb-lb-detail";
      detail.innerHTML = `<td colspan="8"><div class="bb-lb-you__rounds">${roundsChipsHtml(p)}</div></td>`;

      row.addEventListener("click", () => {
        row.classList.toggle("is-open");
        detail.classList.toggle("is-open");
      });

      tbody.appendChild(row);
      tbody.appendChild(detail);
    }

    function appendLbSpacerRow_(tbody) {
      const row = document.createElement("tr");
      row.className = "bb-lb-row bb-lb-row--spacer";
      row.setAttribute("aria-hidden", "true");
      row.innerHTML =
        `<td class="bb-lb-rank">&nbsp;</td>` +
        `<td class="bb-lb-name">&nbsp;<span class="bb-lb-caret">›</span></td>` +
        `<td class="bb-lb-round">&nbsp;</td>` +
        `<td class="bb-lb-round">&nbsp;</td>` +
        `<td class="bb-lb-round">&nbsp;</td>` +
        `<td class="bb-lb-round">&nbsp;</td>` +
        `<td class="bb-lb-round">&nbsp;</td>` +
        `<td class="bb-lb-total">&nbsp;</td>`;
      tbody.appendChild(row);
    }

    function syncLbTableHeight_(visibleRowCount) {
      const stage = lbEl("bb-lb-table-stage");
      if (!stage) return;
      const paged = !lbSearchActive_ && visibleRowCount >= LB_PAGE_SIZE;
      stage.classList.toggle("bb-lb-table-stage--paged", paged);
    }

    function renderLeaderboardRows_(rows, you) {
      const tbody = lbEl("bb-lb-tbody");
      tbody.innerHTML = "";
      const dataRows = rows || [];

      if (!dataRows.length) {
        const tr = document.createElement("tr");
        const msg = lbEmptyMessage_ || "No scores yet — check back once the matches begin.";
        tr.innerHTML = `<td class="bb-lb-empty" colspan="8">${lbEscapeHtml(msg)}</td>`;
        tbody.appendChild(tr);
        syncLbTableHeight_(0);
        return;
      }

      const pageRows = lbSearchActive_ ? dataRows : dataRows.slice(0, LB_PAGE_SIZE);
      pageRows.forEach((p) => appendLbDataRow_(tbody, p, you));
      if (!lbSearchActive_) {
        for (let i = pageRows.length; i < LB_PAGE_SIZE; i += 1) {
          appendLbSpacerRow_(tbody);
        }
      }
      syncLbTableHeight_(pageRows.length);
    }

    function renderLbTablePage_(you, options = {}) {
      const stage = lbEl("bb-lb-table-stage");
      const searchView = options.search || lbSearchActive_;
      const start = lbPage * LB_PAGE_SIZE;
      const slice = searchView ? lbTop100 : lbTop100.slice(start, start + LB_PAGE_SIZE);
      const animate = options.animate !== false &&
        !prefersReducedMotion_() &&
        typeof gsap !== "undefined" &&
        stage &&
        stage.querySelector(".bb-lb-row");

      const paint = () => {
        renderLeaderboardRows_(slice, you);
        updateLbPagination_();
      };

      if (!animate) {
        if (stage && typeof gsap !== "undefined") gsap.killTweensOf(stage);
        if (stage) {
          stage.style.opacity = "";
          stage.style.visibility = "";
        }
        paint();
        lbPageAnimating = false;
        return;
      }

      lbPageAnimating = true;
      updateLbPagination_();
      gsap.killTweensOf(stage);
      gsap.to(stage, {
        autoAlpha: 0,
        duration: 0.18,
        ease: "power1.inOut",
        onComplete: () => {
          paint();
          gsap.to(stage, {
            autoAlpha: 1,
            duration: 0.22,
            ease: "power1.inOut",
            onComplete: () => {
              lbPageAnimating = false;
              updateLbPagination_();
            }
          });
        }
      });
    }

    function changeLbPage_(delta) {
      if (lbPageAnimating) return;
      const nextPage = lbPage + delta;
      if (nextPage < 0 || nextPage >= getLbPageCount_()) return;
      lbPage = nextPage;
      renderLbTablePage_(lbStandingsYou, { animate: true });
    }

    function renderStandings(data, options) {
      if (reviewMode) exitMyBracket();
      lbStandingsYou = data.you || null;
      lastYouPicks = data.you && data.you.picks && Object.keys(data.you.picks).length ? data.you.picks : null;
      const prevPage = lbPage;
      lbTop100Source_ = data.top100 || data.top10 || [];
      lbTop100 = lbTop100Source_.slice();
      lbSearchQuery_ = "";
      lbSearchActive_ = false;
      lbPage = Math.min(prevPage, getLbPageCount_() - 1);
      lbEmptyMessage_ = "";
      renderYouCard(data.you);
      renderLbTablePage_(data.you, { animate: false });
      lbEl("bb-lb-updated").textContent = formatLbUpdated(data.updated_at);
      syncMyBracketButton_();
      setLbStandingsVisible_(true);
      updateChampionPanel_();
      updateLbChrome_();
      if (options && options.autoOpen) {
        lbShouldAutoOpen_ = true;
      }
    }

    function flushPublicLeaderboardAutoOpen_() {
      if (!isLbPublicClosedMode_()) return;

      const openNow = () => {
        showPublicLeaderboardPanel_();
        openLeaderboardSection();
        scheduleAccordionRefresh(leaderboardAccordion);
      };

      openNow();
      window.requestAnimationFrame(openNow);
      window.setTimeout(() => scheduleAccordionRefresh(leaderboardAccordion), 800);
    }

    function flushLeaderboardAutoOpen_() {
      if (isLbPublicClosedMode_()) {
        flushPublicLeaderboardAutoOpen_();
        return;
      }
      if (!lbToken || !lbShouldAutoOpen_) return;
      lbShouldAutoOpen_ = false;

      const openNow = () => {
        setLbStandingsVisible_(true);
        updateLbChrome_();
        openLeaderboardSection();
        scheduleAccordionRefresh(leaderboardAccordion);
      };

      openNow();
      window.requestAnimationFrame(openNow);
      window.setTimeout(() => scheduleAccordionRefresh(leaderboardAccordion), 800);
    }

    function getActiveLbSearchQuery_() {
      return (lbEl("bb-lb-search-email")?.value || "").trim();
    }

    function rowMatchesLbQuery_(row, query) {
      const q = String(query || "").trim().toLowerCase();
      if (!q) return true;
      const haystack = String(row.search_text || row.full_name || "").toLowerCase();
      return haystack.indexOf(q) >= 0;
    }

    function mergePublicStandingsSource_(data) {
      if (!data || !Array.isArray(data.top100)) return;
      lbTop100Source_ = data.top100.slice();
      if (data.updated_at) {
        const updated = lbEl("bb-lb-updated");
        if (updated) updated.textContent = formatLbUpdated(data.updated_at);
      }
      cachePublicStandings_(data);
      // Keep the active search results on screen — background top-100 refreshes
      // must not re-render the table or restart the search fetch.
    }

    function applyPublicSearchResponse_(res, query) {
      const q = String(query || "").trim();
      if (!q || q !== lbSearchQuery_) return;
      lbSearchActive_ = true;
      lbTop100 = res.top100 || [];
      lbEmptyMessage_ = lbTop100.length ? "" : "No matching entries.";
      applyLbSearchMessage_(q, lbTop100.length);
      renderLbSearchResults_();
    }

    function renderPublicStandings_(data, options) {
      if (lbToken) return;
      if (getActiveLbSearchQuery_()) {
        mergePublicStandingsSource_(data);
        return;
      }
      const reveal = !options || options.reveal !== false;
      if (reviewMode) exitMyBracket();
      lbStandingsYou = null;
      lastYouPicks = null;
      lbPage = 0;
      lbTop100Source_ = data.top100 || [];
      lbTop100 = lbTop100Source_.slice();
      lbSearchQuery_ = "";
      lbSearchActive_ = false;
      lbEmptyMessage_ = data.lookup_not_found
        ? "No matching entries."
        : "";

      applyLbSearchMessage_(lbSearchQuery_, lbTop100.length);

      renderYouCard(null);
      renderLbTablePage_(null, { animate: false });
      lbEl("bb-lb-updated").textContent = formatLbUpdated(data.updated_at);
      const mb = lbEl("bb-lb-mybracket-btn");
      if (mb) mb.hidden = true;
      if (reveal) {
        setLbStandingsVisible_(true);
        updateLbChrome_();
      }
    }

    function cachePublicStandings_(data) {
      if (!data || !Array.isArray(data.top100) || !data.top100.length) return;
      try {
        window.localStorage.setItem(
          LB_PUBLIC_CACHE_KEY,
          JSON.stringify({
            top100: data.top100,
            updated_at: data.updated_at || "",
            savedAt: Date.now()
          })
        );
      } catch {
        /* ignore */
      }
    }

    function applyCachedPublicStandings_() {
      if (!isLbPublicClosedMode_()) return false;
      try {
        const raw = window.localStorage.getItem(LB_PUBLIC_CACHE_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.top100) || !data.top100.length) return false;
        renderPublicStandings_(data, { reveal: false });
        return true;
      } catch {
        return false;
      }
    }

    async function preloadPublicStandingsWithRetry_() {
      if (!isLbPublicClosedMode_()) return;
      const searchEmail = (lbEl("bb-lb-search-email")?.value || "").trim();
      if (searchEmail) return;

      warmLeaderboardService_();

      for (let attempt = 1; attempt <= LB_PUBLIC_BOOT_RETRIES; attempt++) {
        try {
          const res = await lbJsonp({ action: "public_standings" });
          if (res && res.ok) {
            if (getActiveLbSearchQuery_()) {
              mergePublicStandingsSource_(res);
              return;
            }
            renderPublicStandings_(res, { reveal: false });
            cachePublicStandings_(res);
            return;
          }
        } catch {
          /* Apps Script may still be waking up */
        }
        if (attempt < LB_PUBLIC_BOOT_RETRIES) {
          await sleepMs_(800 * attempt);
        }
      }
    }

    async function ensurePublicStandingsReady_(mustAwait) {
      if (!isLbPublicClosedMode_()) return;
      const searchEmail = (lbEl("bb-lb-search-email")?.value || "").trim();
      if (searchEmail) return;

      if (lbTop100Source_.length && !mustAwait) {
        preloadPublicStandingsWithRetry_();
        return;
      }

      if (lbTop100Source_.length) return;

      if (lbPublicPreloadPromise_) {
        if (mustAwait) await lbPublicPreloadPromise_;
        return;
      }

      lbPublicPreloadPromise_ = preloadPublicStandingsWithRetry_().finally(() => {
        lbPublicPreloadPromise_ = null;
      });
      if (mustAwait) await lbPublicPreloadPromise_;
    }

    async function preloadPublicStandings_() {
      return ensurePublicStandingsReady_(true);
    }

    function filterLbRowsLocal_(query) {
      const q = String(query || "").trim().toLowerCase();
      if (!q) return lbTop100Source_.slice();
      return lbTop100Source_.filter((row) => rowMatchesLbQuery_(row, q));
    }

    function renderLbSearchResults_() {
      lbPage = 0;
      renderLbTablePage_(null, { animate: false, search: lbSearchActive_ });
      updateLbPagination_();
      scheduleAccordionRefresh(leaderboardAccordion);
    }

    function applyLbSearchMessage_(query, rowCount) {
      const searchMsg = lbEl("bb-lb-search-message");
      if (!searchMsg) return;
      if (!query) {
        searchMsg.hidden = true;
        searchMsg.textContent = "";
        searchMsg.classList.remove("is-error");
        return;
      }
      if (!rowCount && rowCount !== 0) {
        searchMsg.hidden = false;
        searchMsg.textContent = "Searching…";
        searchMsg.classList.remove("is-error");
        return;
      }
      if (!rowCount) {
        searchMsg.hidden = false;
        searchMsg.textContent = "No matching entries.";
        searchMsg.classList.add("is-error");
        return;
      }
      searchMsg.hidden = false;
      searchMsg.textContent = rowCount === 1 ? "1 result" : rowCount + " results";
      searchMsg.classList.remove("is-error");
    }

    function updateLbSearchClearBtn_() {
      const input = lbEl("bb-lb-search-email");
      const clearBtn = lbEl("bb-lb-search-clear");
      if (!clearBtn) return;
      clearBtn.hidden = !((input?.value || "").length);
    }

    function clearLbSearchInput_() {
      const input = lbEl("bb-lb-search-email");
      if (!input) return;
      input.value = "";
      updateLbSearchClearBtn_();
      handleLbSearchInput_();
      input.focus();
    }

    function handleLbSearchInput_() {
      if (!isLbPublicClosedMode_()) return;

      const query = (lbEl("bb-lb-search-email")?.value || "").trim();
      lbSearchQuery_ = query;

      if (!query) {
        lbSearchActive_ = false;
        lbTop100 = lbTop100Source_.slice();
        lbEmptyMessage_ = "";
        applyLbSearchMessage_("", lbTop100.length);
        renderLbSearchResults_();
        window.clearTimeout(lbSearchTimer);
        return;
      }

      lbSearchActive_ = true;
      lbTop100 = [];
      lbEmptyMessage_ = "Searching…";
      applyLbSearchMessage_(query, null);
      renderLbSearchResults_();

      window.clearTimeout(lbSearchTimer);
      lbSearchFetchSeq_ += 1;
      const fetchSeq = lbSearchFetchSeq_;
      lbSearchTimer = window.setTimeout(() => {
        lbFetchSearchResults_(lbSearchQuery_, fetchSeq);
      }, 220);
    }

    async function lbFetchSearchResults_(query, fetchSeq) {
      if (!isLbPublicClosedMode_()) return;
      const q = String(query || "").trim();
      if (!q || q !== lbSearchQuery_) return;
      if (fetchSeq != null && fetchSeq !== lbSearchFetchSeq_) return;

      try {
        const res = await lbJsonp({ action: "public_standings", query: q, email: q });
        if (fetchSeq != null && fetchSeq !== lbSearchFetchSeq_) return;
        if (q !== lbSearchQuery_) return;
        if (res && res.ok) {
          applyPublicSearchResponse_(res, q);
        } else if (res && res.lookup_not_found) {
          applyPublicSearchResponse_(res, q);
        }
      } catch {
        if (fetchSeq != null && fetchSeq !== lbSearchFetchSeq_) return;
        if (q !== lbSearchQuery_) return;
        lbEmptyMessage_ = "Search unavailable right now. Try again in a moment.";
        applyLbSearchMessage_(q, 0);
        renderLbSearchResults_();
      }
    }

    async function preloadPublicStandings_() {
      if (!isLbPublicClosedMode_()) return;
      const searchEmail = (lbEl("bb-lb-search-email")?.value || "").trim();
      if (searchEmail || lbTop100Source_.length) return;
      if (lbPublicPreloadPromise_) return lbPublicPreloadPromise_;

      warmLeaderboardService_();
      lbPublicPreloadPromise_ = lbLoadPublicStandings_("").finally(() => {
        lbPublicPreloadPromise_ = null;
      });
      return lbPublicPreloadPromise_;
    }

    async function lbLoadPublicStandings_(email) {
      if (!isLbPublicClosedMode_()) return;

      try {
        const params = { action: "public_standings" };
        if (email) {
          params.query = email;
          params.email = email;
        }
        const res = await lbJsonp(params);
        if (res && res.ok) {
          if (email) {
            lbSearchQuery_ = email;
            lbSearchActive_ = true;
            applyPublicSearchResponse_(res, email);
          } else {
            renderPublicStandings_(res);
            cachePublicStandings_(res);
          }
        } else if (res && res.reason === "not_closed") {
          setLbStandingsVisible_(false);
          updateLbChrome_();
          updateLeaderboardTriggerCopy_();
        }
      } catch {
        showToast("We couldn't load the leaderboard right now.");
      }
    }

    async function lbRequestCode(event) {
      event.preventDefault();
      clearLbMessage();

      const email = lbEl("bb-lb-email").value.trim();
      if (!email) { showLbMessage("Please enter your email address.", "error"); return; }

      const btn = lbEl("bb-lb-request-btn");
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Checking your entry…";

      try {
        const res = await lbJsonp({ action: "request_code", email });
        if (res && res.ok) {
          lbEmail = email;
          lbEl("bb-lb-sent-email").textContent = email;
          clearLbOtp();
          clearLbMessage();
          showLbModalStep_("code");
          window.setTimeout(() => focusLbOtp(0), 50);
        } else if (res && res.reason === "not_found") {
          showLbMessage("We couldn't find an entry with that email. Use the same address you entered with.", "error");
        } else if (res && res.reason === "email_failed") {
          showLbMessage("We found your entry but couldn't send the login code email. Please try again shortly.", "error");
        } else {
          showLbMessage("Something went wrong sending your code. Please try again.", "error");
        }
      } catch (err) {
        showLbMessage(lbServiceErrorMessage(err), "error");
      } finally {
        btn.disabled = false;
        btn.textContent = original;
      }
    }

    async function lbVerifyCode(event) {
      event.preventDefault();
      clearLbMessage();
      syncLbOtpHidden();

      const code = lbEl("bb-lb-code").value.trim();
      if (!/^\d{6}$/.test(code)) { showLbMessage("Enter the 6-digit code from your email.", "error"); return; }

      const btn = lbEl("bb-lb-verify-btn");
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Checking…";

      try {
        const res = await lbJsonp({ action: "verify_code", email: lbEmail, code });
        if (res && res.ok) {
          saveLbSession_(res.token || null, lbEmail);
          renderStandings(res);
          closeLoginModal_();
          scrollLeaderboardSectionToTop_({ delayMs: 280 });
        } else if (res && res.reason === "bad_code") {
          showLbMessage("That code isn't right. Please check and try again.", "error");
        } else if (res && res.reason === "expired") {
          showLbMessage("That code has expired. Request a new one.", "error");
        } else {
          showLbMessage("We couldn't verify that code. Please try again.", "error");
        }
      } catch (err) {
        showLbMessage(lbServiceErrorMessage(err), "error");
      } finally {
        btn.disabled = false;
        btn.textContent = original;
      }
    }

    async function lbRefresh() {
      if (isLbPublicClosedMode_()) {
        const email = (lbEl("bb-lb-search-email")?.value || "").trim();
        clearLbMessage();
        const btn = lbEl("bb-lb-refresh-btn");
        if (btn) {
          const original = btn.textContent;
          btn.disabled = true;
          btn.textContent = "Refreshing…";
          try {
            await lbLoadPublicStandings_(email);
          } finally {
            btn.disabled = false;
            btn.textContent = original;
          }
        } else {
          await lbLoadPublicStandings_(email);
        }
        return;
      }
      if (!lbToken) return;
      clearLbMessage();

      const btn = lbEl("bb-lb-refresh-btn");
      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Refreshing…";

      try {
        const res = await lbJsonp({ action: "standings", token: lbToken });
        if (res && res.ok) {
          renderStandings(res);
        } else {
          clearLbSession_(false);
          if (isBracketLocked()) {
            await preloadPublicStandingsWithRetry_();
            updateLeaderboardTriggerCopy_();
            updateChampionPanel_();
          } else {
            clearLbSession_();
            openLoginModal_("email");
            showLbMessage("Your session expired. Please log in again.", "error");
          }
        }
      } catch (err) {
        showToast("We couldn't refresh right now. Please try again.");
      } finally {
        btn.disabled = false;
        btn.textContent = original;
      }
    }

    function lbLogout() {
      if (reviewMode) exitMyBracket();
      const mb = lbEl("bb-lb-mybracket-btn");
      if (mb) mb.hidden = true;
      clearLbSession_();
    }

    function lbBackToEmail_() {
      clearLbOtp();
      clearLbMessage();
      showLbModalStep_("email");
      const input = lbEl("bb-lb-email");
      if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 50);
    }

    function clearLbOtp() {
      const hidden = lbEl("bb-lb-code");
      if (hidden) hidden.value = "";
      const otp = lbEl("bb-lb-otp");
      if (!otp) return;
      otp.querySelectorAll(".bb-lb-otp__digit").forEach((input) => {
        input.value = "";
        input.classList.remove("is-filled");
      });
    }

    function focusLbOtp(index) {
      const otp = lbEl("bb-lb-otp");
      if (!otp) return;
      const digits = otp.querySelectorAll(".bb-lb-otp__digit");
      const target = digits[index] || digits[0];
      if (target) target.focus();
    }

    function syncLbOtpHidden() {
      const otp = lbEl("bb-lb-otp");
      const hidden = lbEl("bb-lb-code");
      if (!otp || !hidden) return hidden ? hidden.value : "";
      hidden.value = [...otp.querySelectorAll(".bb-lb-otp__digit")].map((input) => input.value).join("");
      return hidden.value;
    }

    function fillLbOtp(code) {
      const otp = lbEl("bb-lb-otp");
      if (!otp) return;
      const digits = [...otp.querySelectorAll(".bb-lb-otp__digit")];
      const chars = String(code || "").replace(/\D/g, "").slice(0, digits.length);
      digits.forEach((input, i) => {
        input.value = chars[i] || "";
        input.classList.toggle("is-filled", !!input.value);
      });
      syncLbOtpHidden();
    }

    function initLbOtp() {
      const otp = lbEl("bb-lb-otp");
      const codeStep = lbEl("bb-lb-code-step");
      if (!otp || !codeStep) return;

      const digits = [...otp.querySelectorAll(".bb-lb-otp__digit")];

      digits.forEach((input, index) => {
        input.addEventListener("input", (event) => {
          let value = event.target.value.replace(/\D/g, "");

          if (value.length > 1) {
            fillLbOtp(value);
            focusLbOtp(Math.min(value.length, digits.length) - 1);
          } else {
            event.target.value = value.slice(-1);
            event.target.classList.toggle("is-filled", !!event.target.value);
            syncLbOtpHidden();
            if (event.target.value && index < digits.length - 1) {
              digits[index + 1].focus();
            }
          }

          if (syncLbOtpHidden().length === 6) {
            codeStep.requestSubmit();
          }
        });

        input.addEventListener("keydown", (event) => {
          if (event.key === "Backspace" && !input.value && index > 0) {
            digits[index - 1].focus();
          }
        });

        input.addEventListener("focus", () => {
          input.select();
        });
      });

      otp.addEventListener("paste", (event) => {
        event.preventDefault();
        const pasted = (event.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
        if (!pasted) return;
        fillLbOtp(pasted);
        focusLbOtp(Math.min(pasted.length, digits.length) - 1);
        if (syncLbOtpHidden().length === 6) {
          codeStep.requestSubmit();
        }
      });
    }

    function initLeaderboard() {
      const emailStep = lbEl("bb-lb-email-step");
      const codeStep = lbEl("bb-lb-code-step");
      if (!emailStep || !codeStep) return;

      emailStep.addEventListener("submit", lbRequestCode);
      codeStep.addEventListener("submit", lbVerifyCode);
      initLbOtp();
      const backBtn = lbEl("bb-lb-back-btn");
      const refreshBtn = lbEl("bb-lb-refresh-btn");
      const prevBtn = lbEl("bb-lb-prev-btn");
      const nextBtn = lbEl("bb-lb-next-btn");
      const myBracketBtn = lbEl("bb-lb-mybracket-btn");
      if (backBtn) backBtn.addEventListener("click", lbBackToEmail_);
      if (refreshBtn) refreshBtn.addEventListener("click", lbRefresh);
      if (prevBtn) prevBtn.addEventListener("click", () => changeLbPage_(-1));
      if (nextBtn) nextBtn.addEventListener("click", () => changeLbPage_(1));
      if (myBracketBtn) myBracketBtn.addEventListener("click", toggleMyBracket);
      const searchInput = lbEl("bb-lb-search-email");
      if (searchInput) {
        searchInput.addEventListener("input", () => {
          updateLbSearchClearBtn_();
          handleLbSearchInput_();
        });
      }
      const searchClear = lbEl("bb-lb-search-clear");
      if (searchClear) searchClear.addEventListener("click", clearLbSearchInput_);
      initLoginModal_();
      initHeroCta_();

      showLbModalStep_("email");
      setLbStandingsVisible_(false);
      updateLeaderboardTriggerCopy_();
      updateChampionPanel_();
    }

    initHeroCarousel();
    maybeRenderGroups_();
    if (applyCachedBracketDates_()) {
      setBracketDatesReady_();
    }
    applyCachedBracketState_();

    function unblockPageBoot_() {
      const page = document.querySelector(".bb-page");
      if (page) page.classList.remove("is-booting");
      const wf = document.getElementById("bb-page-wf");
      if (wf) wf.hidden = true;
    }

    try {
      setupGroupsAccordion();
      setupLeaderboardAccordion();
      setupScoringAccordion();
      setupBoardAccordion();
      startCountdown();
      initLeaderboard();
      initBracketFromServer();
      startBracketAutoRefresh();
      runAfterFirstPaint_(refreshSubmittedState);
      runAfterFirstPaint_(lazyLoadSummaryBackground_);
    } catch (bootErr) {
      console.error(bootErr);
      unblockPageBoot_();
      markBracketStateReady_();
      bracketInitialLoadPending = false;
      try {
        renderBracket();
        applyLockUi();
      } catch (_) {}
    }
  